/**
 * video-merge-webcodecs.js — hardware merge engine for /video-merge transitions.
 *
 * Replaces the monolithic FFmpeg-WASM xfade graph (which decoded ALL clips at
 * once → OOM on heavy clips, and re-encoded the whole timeline through WASM
 * x264 at ~1.4 fps on Firefox → 18-min waits) with a single decode→composite→
 * encode pass driven by WebCodecs:
 *
 *   per-clip mp4box demux → VideoDecoder(hw) → frames ─┐
 *                                                       ├→ OffscreenCanvas
 *   transition window: 2 decoders, blended per frame ──┘   composite each
 *      → CanvasVideoExport.encode (VideoEncoder hw) → mp4-muxer → MP4
 *
 * Peak concurrency is 2 decoders + 1 encoder (a transition only ever blends
 * clip i and i+1), so memory is bounded regardless of clip count/size — frames
 * are closed the instant they are drawn. HW encode runs 80-110 fps even at
 * 1920×2160 (measured both Chromium and Firefox).
 *
 * Audio is rendered in WINDOWS via OfflineAudioContext (trim,
 * volume/mute, fades, equal-power crossfades over each transition, bg-music).
 *
 *   • TIER C-FAST (Chrome/Edge/Safari 26+): all-WebCodecs, AAC audio in encode().
 *   • TIER C-SPLIT (Firefox — no AAC encoder): WebCodecs video-only + the mixed
 *     AudioBuffer → WAV → one FFmpeg `-c:v copy -c:a aac` pass (video copied;
 *     only the trivial AAC-of-premixed-PCM runs in WASM — seconds, no OOM).
 *
 * Ineligible (caller falls back to the FFmpeg engine, unchanged): any clip not
 * mp4-family (mp4box demuxes ISOBMFF only), codec the VideoDecoder rejects, or
 * no WebCodecs. The probe is up-front so the fallback is clean, never mid-encode.
 *
 * Правка СКОРОСТИ обрабатывается ЗДЕСЬ (с 2026-08-16). Раньше она уводила всю
 * склейку в FFmpeg-WASM — по телеметрии это 189 с в среднем против 8 с, и она
 * была причиной №1 такого ухода (97 случаев за 14 дней; жалоба #1412 — 179 с на
 * 13-секундном ролике). Видео стоит один множитель при выборке кадра (времянка
 * уже делит длительность на speed), звук — через AudioPipeline.stretch: темп
 * БЕЗ сдвига тона, движок по содержимому клипа (речь — WSOLA, музыка — вокодер).
 * ⚠️ Если движок растяжения не поднялся, probe честно возвращает
 * `speed-edit-no-stretch` и работу забирает FFmpeg: подменять его playbackRate'ом
 * нельзя — это «бурундук», то есть тихая потеря качества.
 *
 * Exposes window.VideoMergeWebCodecs:
 *   await probe(clips, opts) → { mode:'fast'|'split'|null, reason, target }
 *   await merge(clips, transitions, target, opts, hooks) → Blob
 *     clips[]:        { file, duration, width, height, fps, hasAudio,
 *                       trimStart, trimEnd, speed, rotation(0/90/180/270),
 *                       volume(0-200), mute, audioFadeIn, audioFadeOut }
 *     transitions[]:  { type, duration }   (length clips.length-1)
 *     target:         { tw, th, fps }       (from the blade's chooseTarget())
 *     opts:           { format:'mp4', crf, bgMusic:{file,volume,loop,start?,env?}|null }
 *                     bgMusic.start — секунда выхода, где звучит начало песни
 *                     (по умолчанию 0); bgMusic.env — [[t, g], …] доля громкости
 *                     музыки в секундах выхода (план VideoMergeMusicPlan: музыка
 *                     затихает на клипах, где человек её выключил).
 *     hooks:          { onProgress(frac), signal, onTransitionMapped(type),
 *                       onOverlay(ctx, tSec, W, H) — необязательный: рисует
 *                       поверх готового кадра (надписи /video-editor) }
 *
 *   Дополнения для монтажной ленты /video-editor (2026-09-30). Все — только по
 *   явной просьбе вызывающего; без них поведение прежнее:
 *     opts.layout     { starts[], effDur[], overlaps[] } — раскладку ленты
 *                     считает ВЫЗЫВАЮЩИЙ, и его же числами живёт превью. Две
 *                     формулы одной ленты (здесь и в модели редактора) —
 *                     ровно то расхождение превью и файла, что уже ловили.
 *     hooks.compose   (ctx, tSec, W, H, frameOf) → кадр рисует вызывающий;
 *                     frameOf(k, srcSec) отдаёт декодированный кадр клипа k.
 *                     Превью редактора рисует тем же кодом.
 *     clip.kind='image' + clip.image() → Promise<drawable>: фото на ленте.
 *     clip.env        [[t, gain], …] — огибающая звука клипа в выходном
 *                     времени вместо встроенной (громкость, затухания, стыки).
 *     opts.audioItems [{ file, start, len, trimStart, trimEnd, loop, env }]
 *                     — свободные звуковые дорожки (музыка, голос).
 *     opts.matroska   WebM/MKV-клипы читаются demuxMatroskaLazy, а не
 *                     отбиваются как «не mp4».
 *     opts.slug       чьё имя носит телеметрия лейна.
 */
(function () {
'use strict';
if (window.VideoMergeWebCodecs) return;

/* ───────── mp4box loader (mirrors webcodecs-frame-extractor.js) ───────── */
function loadMp4Box() {
    if (window.MP4Box) return Promise.resolve();
    if (window.__mp4boxLoadingPromise) return window.__mp4boxLoadingPromise;
    window.__mp4boxLoadingPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = '/vendor/mp4box/mp4box.all.min.js';
        s.onload = function () { resolve(); };
        s.onerror = function () {
            var sources = ['/cdn/jsdelivr/npm/mp4box@0.5.4/dist/mp4box.all.min.js',
                           'https://cdn.jsdelivr.net/npm/mp4box@0.5.4/dist/mp4box.all.min.js'];
            (function next(i) {
                if (i >= sources.length) { reject(new Error('mp4box-load-failed')); return; }
                var s2 = document.createElement('script');
                s2.src = sources[i]; s2.onload = function () { resolve(); };
                s2.onerror = function () { next(i + 1); };
                document.head.appendChild(s2);
            })(0);
        };
        document.head.appendChild(s);
    });
    return window.__mp4boxLoadingPromise;
}

function isMp4Family(file) {
    var n = (file && file.name || '').toLowerCase(), t = (file && file.type || '').toLowerCase();
    return /\.(mp4|m4v|mov|3gp|3g2)$/i.test(n) || /^video\/(mp4|quicktime|3gpp|3gpp2)$/i.test(t);
}

/* ═══════════ Decoder description — the ONE builder in the project ═══════════
   A WebCodecs `description` is simply the codec's configuration box (avcC /
   hvcC / vpcC / av1C) WITHOUT its 8-byte box header. mp4box already parsed that
   box, and it can write it straight back — so the correct record needs no
   per-codec knowledge, no ISO field-packing, and nothing to keep in sync with
   mp4box's field names.

   ⚠️ Two defects this replaces, both measured 2026-08-08 on the real fixture
   corpus (15 files, `wc-desc-shootout`):

   1. HAND-PACKING avcC WAS WRONG ON 100% OF REAL FILES. The previous builder
      copied SPS[0]/PPS[0] into a record sized `7+2+…` while writing only 8
      header bytes, so every record carried ONE uninitialised trailing byte; and
      for High profiles (100/110/122/244) it dropped the three trailing fields
      ISO/IEC 14496-15 §5.3.3.1.2 requires. Measured tails:
        Baseline: hand 41 B vs correct 40 B   (the stray byte)
        High:     hand 44 B vs correct 47 B   (loses `fd f8 f8 00` =
                                               chroma 4:2:0, 8-bit, 8-bit, 0 ext)
      A decoder reading that stray `00` as `chroma_format` sees MONOCHROME.
      Chromium survives it by re-deriving from the SPS — which is exactly why a
      malformed record shipped for so long without a visible symptom.
   2. Non-H.264 got no description at all → `description: null` → TypeError
      (see decoderConfig below).

   Prior art in this repo, both studied before choosing: `reverse-preview.js`
   re-serialises the box via mp4box; `webcodecs-pipeline.js` hand-packs hvcC per
   spec in Waves 18/19 — ~80 lines that must track mp4box's field names
   ("several field-name shapes seen in mp4box revisions"). Neither is used here,
   because measuring both produced something strictly better:

   TIER 1 — slice the payload out of the SOURCE BYTES. mp4box records where each
   box it parsed lives (`start` / `size` / `hdr_size`), so the exact bytes the
   file carried can be handed to the decoder untouched. Nothing is rebuilt, so
   nothing can drift.
   TIER 2 — if the source buffer is unavailable, let mp4box re-serialise. Measured
   on real HEVC: identical except ONE byte — hvcC[13], where mp4box writes the
   four reserved bits as 0 while the file has 1111. Semantically the same
   (min_spatial_segmentation_idc = 0, and reserved bits are ignore-by-spec), but
   "semantically the same" is a claim about every decoder we cannot test, and
   iOS-HEVC is precisely the case we are fixing. So it is the fallback, not the
   default. */
function writeConfigBox(box) {
    var DS = (window.MP4Box && window.MP4Box.DataStream) || window.DataStream;
    if (!DS || !box || typeof box.write !== 'function') return null;
    var st = new DS(undefined, 0, DS.BIG_ENDIAN);
    box.write(st);
    var u8 = new Uint8Array(st.buffer);
    return u8.length > 8 ? u8.subarray(8) : null;
}

function configBoxBytes(box, src) {
    if (!box) return null;
    // Tier 1: the file's own bytes.
    if (src && box.start != null && box.size > (box.hdr_size || 0)) {
        var from = box.start + (box.hdr_size || 0), to = box.start + box.size;
        if (from >= 0 && to <= src.length && to > from) return src.subarray(from, to);
    }
    // Tier 2: re-serialise.
    try { return writeConfigBox(box); } catch (e) { return null; }
}

/* From one stsd sample entry. `src` (Uint8Array over the whole file) is optional
   but preferred — with it the record is byte-exact. Returns null when the entry
   carries no config box (e.g. mpeg4-part2): callers bail to FFmpeg, never guess. */
function buildDecoderDescription(entry, src) {
    if (!entry) return null;
    var box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C;
    if (!box) return null;
    try { return configBoxBytes(box, src); } catch (e) { return null; }
}

/* From a trak, scanning its sample entries — the shape modules that hold a trak
   (reverse-preview, webcodecs-frame-extractor) need in order to adopt this
   builder without reshaping their demux. */
function descriptionFromTrak(trak, src) {
    try {
        var entries = trak.mdia.minf.stbl.stsd.entries;
        for (var i = 0; i < entries.length; i++) {
            var d = buildDecoderDescription(entries[i], src);
            if (d) return d;
        }
    } catch (e) {}
    return null;
}

/* The one place a VideoDecoderConfig is shaped, so no caller can reintroduce the
   null-description throw. `description` is OMITTED when we have none — omitting
   resolves to {supported:false}, passing null throws. */
function decoderConfig(d) {
    var v = (d && d.vtrack && d.vtrack.video) || null;
    var cfg = { codec: d && d.codec, codedWidth: v && v.width, codedHeight: v && v.height };
    if (d && d.desc) cfg.description = d.desc;
    return cfg;
}

/* Dimensions must be present and non-zero: isConfigSupported throws
   "Invalid coded size (0, 0)" otherwise — another silent route to
   `decoder-probe-threw`. */
function hasUsableDims(d) {
    var v = (d && d.vtrack && d.vtrack.video) || null;
    return !!(v && v.width > 0 && v.height > 0);
}

/* ───────── presentation-time zero (the edit list / B-frame delay) ─────────
   A track's composition timestamps do NOT have to start at zero, and on most
   real files they don't: any encoder that emits B-frames shifts every cts by
   the reorder delay and writes an `elst` whose media_time says where the
   presentation actually begins. x264/ffmpeg defaults do it, phones do it.

   A <video> element honours that box — `currentTime = 0` shows the frame at
   media_time. Feeding raw cts into a decoder does not, so a decode lane and a
   <video> preview of the SAME clip disagree by the delay. Measured 2026-08-23
   on a plain `ffmpeg -c:v libx264 -preset veryfast` clip: media_time
   1024/15360 s = 66 667 µs = exactly 2 frames, and the /audio-to-video
   background sat 2 frames behind its own preview for every frame of the
   export. /video-merge and /split-screen inherit the same shift on trim
   points; normalising here fixes all of them at the source.

   Rule: an explicit elst media_time > 0 IS the presentation start (an editor
   may have baked a real trim in there, not just the reorder delay). With no
   elst, the start is the earliest composition timestamp.

   ⚠️ 02.10.2026: when the one reader of edit lists is on the page
   (window.Mp4EditList — the extractor and the pipeline already map through it)
   its plan wins: presentation = media − mediaStart + lead. The rule above
   misses a LEADING EMPTY EDIT (media_time −1, then the media edit — a camera
   track that starts after the movie does): it took the earliest timestamp as
   t=0 and showed every frame early by the lead. It mattered once the per-frame
   tools' frame source moved onto this stream (frame-source.js) — the
   extractor it replaced already honoured the plan. Shapes the reader does not
   model keep the rule above. */
function presentationOffsetUs(vtrack, samples, movieTimescale) {
    var EL = window.Mp4EditList;
    if (EL && typeof EL.plan === 'function' && vtrack && movieTimescale > 0) {
        try {
            var p = EL.plan(vtrack, movieTimescale);
            if (p && p.mediaStartUs != null && (p.shape === 'media' || p.shape === 'empty+media')) {
                return p.mediaStartUs - (p.leadUs > 0 ? p.leadUs : 0);
            }
        } catch (e) {}
    }
    var ts = (vtrack && vtrack.timescale) || 0;
    try {
        var edits = vtrack && vtrack.edits;
        if (edits && edits.length && ts > 0) {
            var mt = edits[0].media_time;
            if (typeof mt === 'number' && mt > 0) return Math.round(mt * 1e6 / ts);
        }
    } catch (e) {}
    if (!samples || !samples.length) return 0;
    var min = Infinity;
    for (var i = 0; i < samples.length; i++) if (samples[i].ts < min) min = samples[i].ts;
    return (min > 0 && isFinite(min)) ? min : 0;
}
/* The shift DecodeStream subtracts from a demuxed clip's raw timestamps. */
function streamOffsetUs(d) {
    return (typeof d.ptsOffsetUs === 'number' ? d.ptsOffsetUs : presentationOffsetUs(d.vtrack, d.samples, d.info && d.info.timescale)) || 0;
}
/* Every picture of the clip, on the presentation clock DecodeStream speaks
   (µs, ascending). A phone in dim light, a screen recording, an edited clip
   change their frame rate inside the file; a tool that re-encodes frame by
   frame needs these times, not «N frames at the average rate» (#9567). */
function presentationTimesUs(d) {
    var s = (d && d.samples) || [], off = streamOffsetUs(d), out = new Array(s.length);
    for (var i = 0; i < s.length; i++) out[i] = s[i].ts - off;
    out.sort(function (a, b) { return a - b; });
    return out;
}

/* ───────── display rotation from the tkhd matrix ─────────
   A <video> element applies the track's display matrix; a VideoDecoder does
   not — it hands back the coded pixels and coded dimensions. So the same
   phone-shot portrait clip comes out upright in a preview and lying on its
   side in any decode lane that forgets to ask. That is comment #370
   («Переворачивает видео»), which webcodecs-pipeline.js already paid for once
   — ~30-50 % of its 299 sessions/7d carried a rotated phone source. The
   reader belongs in the demux result so no decode-lane caller has to know the
   box layout, or remember that the question exists.

   Matrix cells are 16.16 fixed point; the top-left 2×2 carries the rotation.
   Anything not one of the four axis-aligned cases (mirroring, shear) reports
   0 — we do not guess at exotic transforms. */
function VO() { return window.VideoOrientation; }
function trackRotationDeg(mp4, vt) {
    /* Единственный ридер живёт в video-orientation.js — здесь только адрес.
       Копия этой арифметики стояла ещё в трёх файлах, и разъехаться им ничто
       не мешало. */
    var vo = VO();
    if (!vo || !vt) return 0;
    return vo.fromTkhd(mp4, vt.id);
}
/* Размер, в котором файл ДОЛЖЕН быть показан. `d.vtrack.video.width/height` —
   coded-размер, он нужен декодеру и больше никому: раскладку по нему считать
   нельзя, иначе портретная съёмка получает ландшафтный холст. */
function withDisplaySize(d) {
    var vo = VO();
    var cw = (d.vtrack && d.vtrack.video && d.vtrack.video.width) || 0;
    var ch = (d.vtrack && d.vtrack.video && d.vtrack.video.height) || 0;
    var s = vo ? vo.display(cw, ch, d.rotationDeg) : { w: cw, h: ch };
    d.codedWidth = cw; d.codedHeight = ch;
    d.displayWidth = s.w; d.displayHeight = s.h;
    return d;
}

/* Кадр уходит наружу УЖЕ несущим свою ориентацию — это и есть то место, где
   лейн перестаёт требовать от вызывающего помнить про поворот. */
function orientOut(frame, deg) {
    var vo = VO();
    return (vo && deg) ? vo.apply(frame, deg) : frame;
}

// demux(file)            → { info, desc, codec, vtrack, samples, ptsOffsetUs, rotationDeg }  (full — for decoding)
// demux(file, true)      → metadata only (codec/desc/dims), samples:null, resolves at
//                          onReady WITHOUT extracting samples. Used by the eligibility
//                          probe so it never holds a clip's full sample buffer.
function demux(file, metaOnly) {
    return Promise.all([loadOrientation(), loadEditList()]).then(function () { return _demux(file, metaOnly); });
}
/* The one edit-list reader (mp4-edit-list.js), on the same promise the
   extractor and the pipeline use, so a page loads it once. Fail-open: without
   it presentationOffsetUs keeps its own rule. */
function loadEditList() {
    if (window.Mp4EditList) return Promise.resolve();
    if (!window.__mp4EditListLoading) {
        window.__mp4EditListLoading = new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/mp4-edit-list.js') : '/js/mp4-edit-list.js';
            s.onload = function () { resolve(); };
            s.onerror = function () { reject(new Error('load: mp4-edit-list.js')); };
            document.head.appendChild(s);
        });
    }
    return window.__mp4EditListLoading.then(function () {}, function () {});
}
/* ── Демукс клипа для ЭТОГО лейна: ленивый, с откатом на жадный (2026-09-11) ──
   `demux()` читает ВЕСЬ файл одним FileReader'ом в ArrayBuffer и держит копию
   каждого сэмпла (≈2.7 × размер файла в куче) — даже для пробы, которой нужны
   только ftyp+moov. `demuxLazy()` (таблица сэмплов сейчас, байты — срезами по
   ≤8 МБ по ходу декода) существовал с 2026-08 и работал в проде у
   /slideshow-maker и /video-insert, но собственный лейн склейки его не звал —
   тот же класс «половина дверей». Замер 10.09.2026: два клипа по 1.3 ГБ в
   быстром лейне — оба движка молча стояли 80 минут на чтении файла (CPU 0,
   выход не растёт), а ленивый путь видео-части не трогает вовсе.
   Откат на жадный — для файлов, у которых сканер боксов не находит moov так,
   как его находит mp4box (обрезанные/склеенные хвосты): прежнее поведение. */
function demuxForLane(file, metaOnly, slug, eagerMax) {
    slug = slug || 'video-merge';
    return demuxLazy(file).then(function (d) {
        if (!metaOnly) { try { window.__ae && window.__ae('feature_use', { slug: slug, f: 'wc_demux', mode: 'lazy', szMB: Math.round((file.size || 0) / 1048576) }); } catch (_) {} }
        return d;
    }, function (e) {
        /* The eager reader holds ~2.7x the file in memory. A caller on a budget
           (a phone tab) names its ceiling, and a bigger file is refused here
           instead of being read whole: the caller's own floor is cheaper than
           a killed tab. */
        if (!metaOnly && eagerMax > 0 && (file.size || 0) > eagerMax) {
            var big = new Error('demux: no lazy table (' + String(e && e.message || e).slice(0, 40) + ') and the file is over the eager ceiling');
            big.code = 'eager-ceiling';
            throw big;
        }
        try { window.__ae && window.__ae('feature_use', { slug: slug, f: 'wc_demux', mode: 'eager', meta: metaOnly ? 1 : 0, szMB: Math.round((file.size || 0) / 1048576), why: String(e && e.message || e).slice(0, 60) }); } catch (_) {}
        return demux(file, metaOnly);
    });
}
/* Демукс клипа ЛЕНТЫ: mp4-семейство — прежним путём; WebM/MKV — ленивой
   таблицей Matroska, но только если вызывающий об этом попросил
   (opts.matroska). VP9 в контейнере не называет профиль: первым идёт
   профиль 0, и если декодер его не берёт — 10-битный профиль 2
   (altCodec), как у seek-preview. */
async function demuxClip(file, metaOnly, opts) {
    if (opts && opts.matroska && !isMp4Family(file) && isMatroska(file)) {
        var d = await demuxMatroskaLazy(file);
        if (d.altCodec && typeof VideoDecoder !== 'undefined') {
            var ok = false;
            try { var s = await VideoDecoder.isConfigSupported(decoderConfig(d)); ok = !!(s && s.supported); } catch (e) { ok = false; }
            if (!ok) d.codec = d.altCodec;
        }
        return d;
    }
    return demuxForLane(file, metaOnly, opts && opts.slug, opts && opts.eagerMax);
}
function clipIsImage(c) { return !!(c && c.kind === 'image'); }
function _demux(file, metaOnly) {
    return new Promise(function (resolve, reject) {
        var mp4 = MP4Box.createFile(), samples = [], info = null, desc = null, codec = null, vtrack = null, done = false;
        // Kept so the description can be sliced from the ORIGINAL bytes (tier 1
        // of configBoxBytes) rather than rebuilt. Set before appendBuffer, which
        // is what triggers onReady.
        var srcBytes = null;
        function rej(e) { if (!done) { done = true; reject(e); } }
        mp4.onError = function (e) { rej(new Error('mp4box:' + e)); };
        mp4.onReady = function (inf) {
            info = inf;
            var vt = inf.tracks.filter(function (t) { return t.video; })[0];
            if (!vt) { rej(new Error('no-video-track')); return; }
            codec = vt.codec; vtrack = vt;
            try { desc = buildDecoderDescription(mp4.getTrackById(vt.id).mdia.minf.stbl.stsd.entries[0], srcBytes); } catch (e) {}
            if (metaOnly) { if (!done) { done = true; resolve(withDisplaySize({ info: info, desc: desc, codec: codec, vtrack: vtrack, samples: null,
                                                                ptsOffsetUs: presentationOffsetUs(vtrack, null, info && info.timescale),
                                                                rotationDeg: trackRotationDeg(mp4, vt) })); } return; }
            mp4.setExtractionOptions(vt.id, null, { nbSamples: 1000000 }); mp4.start();
        };
        mp4.onSamples = function (id, user, s) {
            for (var i = 0; i < s.length; i++) {
                var x = s[i];
                samples.push({ type: x.is_sync ? 'key' : 'delta',
                    ts: Math.round(x.cts * 1e6 / x.timescale),
                    dur: Math.round(x.duration * 1e6 / x.timescale), data: x.data });
            }
        };
        var reader = new FileReader();
        reader.onload = function () { var b = reader.result; b.fileStart = 0; srcBytes = new Uint8Array(b);
            /* mp4box throws from inside appendBuffer on a box it cannot read; uncaught here, the
               promise never settled and the caller waited forever (Sentry TIMBRICA-FRONTEND-R9) */
            try { mp4.appendBuffer(b); mp4.flush(); } catch (e) { rej(e); return; }
            if (done) return;
            /* metaOnly resolves inside onReady. If the whole file has been fed and
               onReady never fired, there is no moov to read — and mp4box does not
               always call onError for that. Settling here is what keeps the contract
               "resolves or rejects, never neither".
               ⚠️ Measured 2026-08-08 on tests/playwright/fixtures/video-corrupt.mp4:
               without this, MusicVideoWebCodecs.probe() hung FOREVER (>15 s and
               counting) because it awaits demux(file, true) with no timeout of its
               own — the render sat on «Рендерим клип…» until the 120 s stall
               watchdog killed it, instead of falling back to FFmpeg in milliseconds.
               (music-video-maker's clip probe races a 10 s timeout and so hid this.) */
            done = true;
            if (metaOnly) reject(new Error('no-moov'));
            else if (!healSampleTimes(samples)) reject(new Error('bad-timescale'));   // `done` is already set: rej() would swallow it
            else resolve(withDisplaySize({ info: info, desc: desc, codec: codec, vtrack: vtrack, samples: samples,
                           ptsOffsetUs: presentationOffsetUs(vtrack, samples, info && info.timescale),
                           rotationDeg: trackRotationDeg(mp4, vtrack) })); };
        reader.onerror = function () { rej(new Error('filereader')); };
        reader.readAsArrayBuffer(file);
    });
}

/* A sample table WebCodecs can take: every `ts` a finite integer and every `dur`
   a whole, non-negative number of µs (EncodedVideoChunkInit.duration is an
   [EnforceRange] unsigned long long — NaN throws). mp4box leaves `duration`
   undefined for the samples past the end of a short `stts` (a damaged or cut
   file; measured 04.11: 30 samples, stts covering 25 → 5 durations NaN), and
   DecodeStream threw «Failed to read the 'duration' property … not of type
   'unsigned long long'» on the first of them (Sentry SF, /video-editor export).
   Those samples have no TIME either (their dts is summed from the same stts), and
   the table is in decode order, so they are the TAIL: it is dropped — no earlier
   sample references a later one in decode order, so what stays decodes as before
   (FFmpeg extrapolates the last delta instead; a few untimed frames at the very
   end are not worth guessing ctts offsets for). A missing duration becomes the gap
   to the next frame in presentation order, or the median of the known ones for
   the last frame — the same rule as webm-copy-trim.js fillDurations. A track with
   no timed sample at all (timescale 0) cannot be timed: false, and the caller
   declines the clip. */
function healSampleTimes(samples) {
    var i, s, clean = true;
    for (i = 0; i < samples.length; i++) {
        s = samples[i];
        if (!isFinite(s.ts)) break;
        if (clean && !(s.dur >= 0 && s.dur === Math.round(s.dur))) clean = false;
    }
    if (i === 0) return false;
    if (clean && i === samples.length) return true;   // the usual file: nothing to heal, no sort
    samples.length = i;
    var order = samples.slice().sort(function (a, b) { return a.ts - b.ts; }), known = [];
    for (i = 0; i < order.length; i++) {
        s = order[i];
        if (!(s.dur >= 0) || !isFinite(s.dur)) s.dur = i + 1 < order.length ? Math.max(0, order[i + 1].ts - s.ts) : -1;
        else s.dur = Math.round(s.dur);
        if (s.dur > 0) known.push(s.dur);
    }
    known.sort(function (a, b) { return a - b; });
    for (i = 0; i < order.length; i++) if (order[i].dur < 0) order[i].dur = known.length ? known[known.length >> 1] : 0;
    return true;
}

/* Sequential live-frame decoder, bounded by a high-water mark. Optionally
   feeds only the window [startUs, endUs] (for trimmed clips): it begins at the
   last keyframe ≤ startUs so the decode chain is valid, and stops feeding past
   endUs. Frames before startUs are decoded but dropped by the cursor. */
/* ─────────── demuxLazy: the sample TABLE now, the sample BYTES on demand ───────────
   demux() above reads the whole file through a FileReader and keeps a copy of
   every sample, so it retains roughly 2.7x the file size. That is why every
   caller carries a size cap above which it must fall back to something worse.
   For /audio-to-video's video background that fallback is HTMLVideoElement
   seeking — 49-91 ms/frame, and not frame-exact at any price. Measured on a
   372 MB clip 2026-08-30: the exported background was near-black for the first
   10.5 s (no seek ever landed) and then FROZE on one held frame for the rest.
   That is the "изображение стоит на месте" report, produced by us.

   The bytes never had to be in memory. A File is disk-backed and the sample
   table carries ABSOLUTE file offsets, so the decoder can be fed by slice-read.
   Measured on that same 372 MB file:
     · ftyp+moov is 5.4 KB and parses in 9 ms, against 372 MB / 655 ms eager,
     · retention is O(1) rather than O(file),
     · a slice read costs 0.79 ms (37 MB/s small samples, 210 MB/s large).
   Verified byte-for-byte against demux() over all 288 samples of that file —
   payloads, timestamps, durations and key flags identical, 0 mismatches.

   ⚠️ mp4box's stream must be CONTIGUOUS from 0, and moov sits BEHIND the mdat
   on any file not written with faststart (it did here: mdat@40+389559131,
   moov@389559171). Appending ftyp and moov at their true fileStarts leaves a
   389 MB hole and onReady never fires. They are therefore appended BACK TO
   BACK as a synthetic stream — chunk offsets in stco/co64 are absolute file
   offsets and stay correct wherever the moov is presented. The same buffer is
   handed to buildDecoderDescription as `src` so box.start lines up with it;
   configBoxBytes re-serialises if it ever doesn't, so a mismatch degrades
   instead of producing a wrong description. */
function topLevelBoxes(file) {
    /* Walks only the 8/16-byte box headers — 4-6 ms on a 372 MB file, because
       nothing between them is read. */
    return (async function () {
        var boxes = [], off = 0, guard = 0;
        while (off < file.size && guard++ < 4096) {
            var h = new DataView(await file.slice(off, off + 16).arrayBuffer());
            if (h.byteLength < 8) break;
            var size = h.getUint32(0), hdr = 8;
            var type = String.fromCharCode(h.getUint8(4), h.getUint8(5), h.getUint8(6), h.getUint8(7));
            if (size === 1) {
                if (h.byteLength < 16) break;
                size = Number(h.getBigUint64(8)); hdr = 16;
            } else if (size === 0) {
                size = file.size - off;           // "to end of file"
            }
            if (!(size >= hdr) || off + size > file.size) break;
            boxes.push({ type: type, start: off, size: size });
            off += size;
        }
        return boxes;
    })();
}
async function demuxLazy(file) {
    await Promise.all([loadOrientation(), loadEditList()]);
    var boxes = await topLevelBoxes(file);
    var moov = null, ftyp = null;
    for (var i = 0; i < boxes.length; i++) {
        if (boxes[i].type === 'moov' && !moov) moov = boxes[i];
        if (boxes[i].type === 'ftyp' && !ftyp) ftyp = boxes[i];
    }
    if (!moov) throw new Error('no-moov');
    /* One buffer holding ftyp+moov back to back: it is both what mp4box is fed
       and what the description is sliced out of, so the two cannot disagree. */
    var parts = [], total = 0;
    var wanted = ftyp ? [ftyp, moov] : [moov];
    for (var k = 0; k < wanted.length; k++) {
        var u = new Uint8Array(await file.slice(wanted[k].start, wanted[k].start + wanted[k].size).arrayBuffer());
        parts.push(u); total += u.length;
    }
    var head = new Uint8Array(total), at = 0;
    for (var m = 0; m < parts.length; m++) { head.set(parts[m], at); at += parts[m].length; }

    var mp4 = MP4Box.createFile();
    var info = await new Promise(function (resolve, reject) {
        var settled = false;
        mp4.onError = function (e) { if (!settled) { settled = true; reject(new Error('mp4box:' + e)); } };
        mp4.onReady = function (inf) { if (!settled) { settled = true; resolve(inf); } };
        try {
            var ab = head.buffer.slice(0);
            ab.fileStart = 0;
            mp4.appendBuffer(ab);
            mp4.flush();
        } catch (e) { if (!settled) { settled = true; reject(e); } }
        /* Same contract as demux(): resolve or reject, never neither. A moov
           that parses to no tracks would otherwise hang the caller forever. */
        if (!settled) { settled = true; reject(new Error('no-moov')); }
    });
    var vt = info.tracks.filter(function (t) { return t.video; })[0];
    if (!vt) throw new Error('no-video-track');

    var si = mp4.getTrackSamplesInfo(vt.id);
    if (!si || !si.length) throw new Error('no-samples');
    var samples = new Array(si.length);
    for (var j = 0; j < si.length; j++) {
        var x = si[j];
        samples[j] = {
            type: x.is_sync ? 'key' : 'delta',
            ts: Math.round(x.cts * 1e6 / x.timescale),
            dur: Math.round(x.duration * 1e6 / x.timescale),
            offset: x.offset, size: x.size
        };
    }
    if (!healSampleTimes(samples)) throw new Error('bad-timescale');
    var desc = null;
    try { desc = buildDecoderDescription(mp4.getTrackById(vt.id).mdia.minf.stbl.stsd.entries[0], head); } catch (e) {}
    return withDisplaySize({
        info: info, desc: desc, codec: vt.codec, vtrack: vt, samples: samples,
        ptsOffsetUs: presentationOffsetUs(vt, samples, info && info.timescale),
        rotationDeg: trackRotationDeg(mp4, vt),
        // What makes DecodeStream read bytes instead of expecting them.
        lazy: true, file: file
    });
}

/* Ориентация — такая же зависимость лейна, как mp4box, поэтому он забирает её
   сам. Блейд, забывший подключить скрипт, не должен превращаться в молча
   повёрнутое видео у пользователя. */
function loadOrientation() {
    if (window.VideoOrientation) return Promise.resolve();
    if (window.__voLoadingPromise) return window.__voLoadingPromise;
    window.__voLoadingPromise = new Promise(function (resolve) {
        var s = document.createElement('script');
        /* Версионированный адрес: nginx отдаёт /js/… immutable на 180 дней,
           и без версии вернувшийся посетитель никогда не получит правку. */
        s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/video-orientation.js') : '/js/video-orientation.js';
        s.onload = function () { resolve(); };
        s.onerror = function () { resolve(); };   // fail-open: без него лейн работает как раньше
        document.head.appendChild(s);
    });
    return window.__voLoadingPromise;
}

/* ── «Decodes this clip» is a verdict earned by decoding (2026-10-30) ──────────
   The probe asked VideoDecoder.isConfigSupported — an opinion. Yandex 24.10 on
   Windows 7/8 without a GPU adapter (platformVersion 0.1.0, 2–4 GB) and Yandex on
   Linux answer «supported» for every H.264 profile and then refuse configure()
   with «Unsupported configuration. Check isConfigSupported() prior to calling
   configure()» — after the export had started (RU, October: 49 sessions in 9 tools,
   12 of them /video-editor, 10 without a file; their <video> plays the same file,
   so the preview looked fine). DecoderVerify (decoder-verify.js) decodes one real
   keyframe of the clip on every hint and answers yes, no, or «no verdict» — a
   clean refusal names the clip, and the caller can lift it into a codec this
   browser does decode. Fail-open: no module, no keyframe — the old optimism. */
function loadDecoderVerify() {
    if (window.DecoderVerify) return Promise.resolve();
    if (window.__dvLoadingPromise) return window.__dvLoadingPromise;
    window.__dvLoadingPromise = new Promise(function (resolve) {
        var s = document.createElement('script');
        s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/decoder-verify.js') : '/js/decoder-verify.js';
        s.onload = function () { resolve(); };
        s.onerror = function () { resolve(); };
        document.head.appendChild(s);
    });
    return window.__dvLoadingPromise;
}
async function clipDecodeVerdict(d, slug) {
    await loadDecoderVerify();
    if (!window.DecoderVerify || !d || !d.samples || !d.samples.length) return { ok: true, uncertain: true, reason: 'no-verifier' };
    var k = -1;
    for (var i = 0; i < d.samples.length && i < 600; i++) { if (d.samples[i].type === 'key') { k = i; break; } }
    if (k < 0) return { ok: true, uncertain: true, reason: 'no-keyframe' };
    var s = d.samples[k], data = s.data || null;
    try {
        if (!data && d.lazy && d.file && s.size > 0) data = new Uint8Array(await d.file.slice(s.offset, s.offset + s.size).arrayBuffer());
    } catch (e) { data = null; }
    if (!data) return { ok: true, uncertain: true, reason: 'no-keyframe-bytes' };
    var cfg = decoderConfig(d);
    var v = await window.DecoderVerify.pick({
        codec: cfg.codec, codedWidth: cfg.codedWidth, codedHeight: cfg.codedHeight, description: cfg.description,
        keyframe: { data: data, timestamp: s.ts || 0, duration: s.dur || 0 }, slug: slug
    });
    return v || { ok: true, uncertain: true, reason: 'no-answer' };
}

/* ─────── demuxMatroskaLazy: the same lazy table for WebM / Matroska (2026-09-24) ───────
   This lane read mp4 only, so a WebM background in /audio-to-video fell to the
   per-frame <video> seek lane — the lane that has now shipped wrong pictures
   three times. The sample table comes from WebmCopyTrim's EBML walker
   (webm-copy-trim.js: Matroska is parsed in ONE place, the trim door's), and
   DecodeStream gets exactly what demuxLazy gives it: {type, ts, dur, offset,
   size} plus the File to slice. Codec strings follow seek-preview.js, whose
   bench decoded them on real files; a VP9 stream carries no profile in the
   container, so profile 0 goes first and profile 2 (10-bit) is the fallback. */
function isMatroska(file) {
    var n = (file && file.name || '').toLowerCase(), t = (file && file.type || '').toLowerCase();
    return /\.(webm|mkv)$/i.test(n) || /^video\/(webm|x-matroska|matroska)/i.test(t);
}
function loadWebmReader() {
    if (window.WebmCopyTrim && window.WebmCopyTrim.videoSamples) return Promise.resolve();
    if (window.__webmReaderLoading) return window.__webmReaderLoading;
    window.__webmReaderLoading = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/webm-copy-trim.js') : '/js/webm-copy-trim.js';
        s.onload = function () { resolve(); };
        s.onerror = function () { window.__webmReaderLoading = null; reject(new Error('webm-reader-load')); };
        document.head.appendChild(s);
    });
    return window.__webmReaderLoading;
}
function hex2(n) { return ('0' + (n & 255).toString(16)).slice(-2); }
function av1CodecOf(p) {
    if (!p || p.length < 4) return 'av01.0.08M.08';
    var prof = (p[1] >> 5) & 7, lvl = p[1] & 31, tier = (p[2] >> 7) & 1, hbd = (p[2] >> 6) & 1, twelve = (p[2] >> 5) & 1;
    return 'av01.' + prof + '.' + ('0' + lvl).slice(-2) + (tier ? 'H' : 'M') + '.' + (twelve ? '12' : hbd ? '10' : '08');
}
function matroskaCodec(id, priv) {
    if (id === 'V_VP9') return { codec: 'vp09.00.51.08', alt: 'vp09.02.51.10', desc: null };
    if (id === 'V_VP8') return { codec: 'vp8', desc: null };
    if (id === 'V_AV1') return { codec: av1CodecOf(priv), desc: null };
    if (id === 'V_MPEG4/ISO/AVC' && priv && priv.length >= 4) return { codec: 'avc1.' + hex2(priv[1]) + hex2(priv[2]) + hex2(priv[3]), desc: priv };
    return null;   // HEVC in Matroska, MPEG-4 Part 2, … — the seek lane keeps them
}
async function demuxMatroskaLazy(file) {
    await loadOrientation();
    await loadWebmReader();
    var v = await window.WebmCopyTrim.videoSamples(file);
    var cc = matroskaCodec(v.codecId, v.codecPrivate);
    if (!cc) { var e = new Error('mkv-codec:' + String(v.codecId || '?').slice(0, 20)); e.code = 'codec'; throw e; }
    var samples = new Array(v.samples.length), min = Infinity;
    for (var i = 0; i < v.samples.length; i++) {
        var x = v.samples[i];
        samples[i] = { type: x.key ? 'key' : 'delta', ts: x.ts, dur: x.dur, offset: x.offset, size: x.size };
        if (x.ts < min) min = x.ts;
    }
    /* The same WebCodecs contract as the mp4 tables (SF came back on 04.11 from
       /remove-text-from-video, whose frame source demuxes WebM too). */
    if (!healSampleTimes(samples)) throw new Error('bad-timescale');
    /* DecodeStream feeds from index 0; a first frame that is not a keyframe
       cannot be decoded, and the decoder says so only on the first chunk. */
    if (samples[0].type !== 'key') { var k = new Error('mkv-first-not-key'); k.code = 'first-not-key'; throw k; }
    return withDisplaySize({
        info: null, desc: cc.desc, codec: cc.codec, altCodec: cc.alt || null,
        vtrack: { video: { width: v.width, height: v.height } }, samples: samples,
        ptsOffsetUs: isFinite(min) ? min : 0, rotationDeg: 0,
        lazy: true, file: file, container: 'matroska'
    });
}

/* ── A decoder that dies MID-STREAM is not the end of the clip (2026-09-26) ──
   Until now one decoder error rejected next() and took the whole job with it.
   Prod RU, 30 d: the transition lane of /video-merge lost 19 runs to «Decoding
   error.», 17 to «Decoder failure», 10 to «Decoding task did not complete», and
   the per-clip normalize ~30 more; report of 2026-09-25 lost a 43-minute
   re-encode to one «Decoding error.» near its end, on a 2-core laptop.
   Now the stream builds a FRESH decoder and goes on: first a fresh hardware one
   (a GPU hiccup — the GPU process restarted, the codec was reclaimed), then the
   software one (a stream this hardware cannot take). On Android there is no
   software H.264 decoder and that rung fails on its first chunk — which is why
   hardware comes first here (DecoderHintLadderConventionTest). The new decoder
   starts at the last keyframe at or before the newest frame already given out,
   and every frame up to that one is dropped, so a consumer never sees a frame
   twice or out of order. Two rescues per stream; the third error is the error.
   Guard: tests/playwright/decode-stream-survives-a-dying-decoder.mjs. */
var DECODE_RESCUE_HINTS = ['prefer-hardware', 'prefer-software'];
/* ── How many chunks may WAIT inside the decoder (2026-09-26) ──
   The feed used to stop only when HWM decoded frames sat unconsumed. When the
   DECODER is the slow side — 4K on a weak GPU, the software decoder — frames
   never pile up, so the feed never stopped: measured on a 1080p + 4K fade merge,
   229 of 480 and 206 of 336 chunks were inside the decoder at once
   (decodeQueueSize up to 224), i.e. about half of each clip, and a 4-minute clip
   of 250–300 MB would sit there almost whole — on the 4 GB laptops where the
   long 4K runs die. WebCodecs has its own backpressure for this: decodeQueueSize
   counts requests the codec has not taken yet, and `dequeue` fires when it takes
   one. The codec takes input whenever it can use it (including when it needs
   more before it can emit a frame), so waiting on the queue cannot deadlock the
   way waiting on OUTPUTS would. A decoder without the `dequeue` event keeps the
   old feed: gating without a wake-up is how a stream stops for good. */
var DECODE_QUEUE_MAX = 8;
/* ── A decoder that goes SILENT is a decoder that died (2026-10-02) ──
   The error callback is how a codec says it died; some never say it. The
   Kirin/Yandex wedge takes every chunk and never hands back a frame — no error,
   no output — and next() then waited forever: a stream without an error had no
   way to end. Silence is measured only where it means something:
     · only while a caller WAITS in next(). A consumer busy with the previous
       frame is never the decoder's fault — /video-bg-remover's frame source
       blamed exactly that on the decoder 448 times in two weeks (RU+intl,
       18.09–02.10: queue 1, fed − decoded 3–10, «silence» 16–100 s that was the
       model on a phone; webcodecs-frame-extractor.js), and every one of those
       dropped the clip to <video> seeking;
     · only in VISIBLE time (timbrica-core.js __visibleNow) — a hidden tab is
       not a dead codec;
     · not while the stream is reading the file (that wait is the disk's).
   SILENT_MS of it is handled like any decoder error: the same rescue — a fresh
   decoder from the last keyframe — and the error after the last rescue. A read
   that never answers is an error of its own: no fresh decoder helps a dead
   file handle. */
var DECODE_SILENT_MS = 12000;
/* ── Frames leave in PRESENTATION order, whatever order the decoder uses ──
   WebCodecs promises output in presentation order, and Chromium keeps it.
   Safari up to 26 did not: «Fixed an issue where the WebCodecs VideoDecoder API
   output frames in an incorrect order for videos containing B-frames» is in
   the Safari 27.0 release notes (webkit.org/blog/18325, 17.09.2026), so every
   iPhone on iOS 26 or older hands a B-frame clip back in DECODE order. A cursor
   that walks forward then holds a later picture over earlier timestamps and
   throws the earlier ones away — no error anywhere, only a jerky result.
   The sample table already knows every presentation time of the fed range, so
   the order is restored exactly: a frame is given out when every frame before
   it has been. A frame the decoder never produces cannot hold the stream
   hostage: past REORDER_SLACK early frames the earliest goes out anyway.
   Frames are HELD only on WebKit. Chromium and Firefox keep the spec's order,
   and there a hold could only ever wait for a picture the decoder chose not to
   produce (the leading pictures of an open-GOP keyframe decoded cold) — while
   the held frames keep the surfaces of a hardware decoder's fixed pool.
   `window.__DS_REORDER` forces it either way (benches). */
var REORDER_SLACK = 16;
function reorderNeeded() {
    if (window.__DS_REORDER != null) return !!window.__DS_REORDER;
    try { var c = window.__deviceCap && window.__deviceCap.get && window.__deviceCap.get(); if (c && typeof c.isWebKit === 'boolean') return c.isWebKit; } catch (e) {}
    var ua = (window.navigator && navigator.userAgent) || '';
    return /AppleWebKit\//.test(ua) && !/(Chrome|Chromium)\/|Edg\/|OPR\/|Firefox\//.test(ua);
}
/* ── A cold start is not fed its keyframe's LEADING pictures (2026-11-04) ──
   Complaint 31.10 (iPhone, Yandex, /remove-text-from-video, a 117-s HEVC .mov):
   «20 minutes, then "try again later"» — «Decoder failure». An iPhone's HEVC is an
   open GOP: the first sync sample is an IDR, every later one a CRA whose RASL pictures
   follow it in decode order, are shown before it and reference the previous group.
   Every stream that starts at a keyframe other than the first — a trimmed window, the
   seek floor's one frame, a rescue — fed them to a decoder that had not seen that
   group. WebKit answers the first picture it cannot build with «Decoder failure» and
   closes the decoder, so both rescues (they restart at a keyframe too) died the same
   way: RU 03.11, /remove-text `from` 165 of 174 and /remove-object 111 of 139, 0 frames
   out; the passes that started at frame 0 decoded the same files cleanly. The rule —
   which samples, and why they are never needed — has one owner: __leadingPictures
   (timbrica-core.js). Returned as a set of indices for the feed to step over. */
function leadingAfter(s, k, hi) {
    var set = {};
    if (typeof window.__leadingPictures !== 'function') return set;
    var L = window.__leadingPictures(Math.min(hi, s.length), k,
        function (i) { return s[i].ts; }, function (i) { return s[i].type === 'key'; });
    for (var i = 0; i < L.length; i++) set[L[i]] = 1;
    return set;
}
function DecodeStream(d, startUs, endUs, opts) {
    this.d = d; this.q = []; this.waiters = []; this.err = null; this.done = false; this.flushed = false;
    this.gen = 0; this.rescues = 0; this.lastTs = -Infinity; this.dropUpTo = -Infinity; this.closed = false;
    /* Единственное место, через которое проходит КАЖДЫЙ кадр этого модуля —
       поэтому ориентация применяется здесь, а не у полутора десятка
       вызывающих. */
    this.rotationDeg = (d && d.rotationDeg) || 0;
    /* Decoded frames waiting for the consumer. Ten keep a merge's encoder fed;
       a consumer that spends a second per frame (a model on a phone) gains
       nothing from them and pays for each — a held VideoFrame is a decoder
       surface, 3 MB at 1080p and 12 MB at 4K — so it may ask for fewer. */
    this.HWM = (opts && opts.hwm > 0) ? Math.round(opts.hwm) : 10;
    /* Кадр уходит из ClipCursor повёрнутым по-настоящему (VideoOrientation.upright):
       на движке без нативного поворота (Firefox) иначе его поворот «едет рядом»,
       и любой обычный drawImage кладёт съёмку с телефона боком. Потребитель,
       который САМ рисует через VideoOrientation.draw/drawSource/size, просит
       `deferRotation` и копии не платит — список таких держит
       VideoDecodeLanesDeclareOrientationTest. */
    this.deferRotation = !!(opts && opts.deferRotation);
    this.silentMs = (opts && opts.silentMs >= 0) ? opts.silentMs : DECODE_SILENT_MS;
    /* startUs/endUs — and every timestamp this stream emits — are PRESENTATION
       time, the same clock a <video> element and the tool's UI speak. Raw cts
       is shifted by the edit list / B-frame reorder delay; see
       presentationOffsetUs. Callers never see the raw clock. */
    this.off = streamOffsetUs(d);
    var off = this.off;
    // find feed range
    var s = d.samples, lo = 0, hi = s.length;
    if (startUs > 0) { for (var i = 0; i < s.length; i++) { if (s[i].type === 'key' && (s[i].ts - off) <= startUs) lo = i; if ((s[i].ts - off) > startUs) break; } }
    /* The END of the feed is the last sample in DECODE order whose picture is
       shown by endUs — not the first one shown after it. With B-frames a P
       frame past the end is decoded BEFORE the B frames that precede it on
       screen, and the old «first sample past endUs» cut the feed right after
       that P: the last 2–3 pictures of a trimmed window were never decoded and
       the cursor handed out a picture from after the trim (measured
       2026-10-02, frame-source-pulls-the-decoder.mjs R: x265 clip, 0–4 s at
       15 fps, target 118 got frame 121). One sample past the end stays in, as
       before; the scan stops a reorder window after the last shown one. */
    if (endUs != null) {
        var last = -1, past = -1, beyond = 0;
        for (var j = lo; j < s.length; j++) {
            if ((s[j].ts - off) <= endUs) { last = j; beyond = 0; }
            else { if (past < 0) past = j; if (++beyond > REORDER_SLACK) break; }
        }
        hi = Math.max(last, past) + 1;
        if (hi <= lo) hi = s.length;
    }
    this.lo = lo; this.hi = Math.min(hi, s.length); this.fed = lo;
    /* The decoder starts COLD at `lo`: the pictures of that group shown before
       its keyframe are never fed (see leadingAfter) — and never waited for. */
    this.leadSkipped = 0;
    this.lead = leadingAfter(s, lo, this.hi);
    // the presentation order of everything this stream will decode
    var ord = [];
    for (var k = this.lo; k < this.hi; k++) if (!this.lead[k]) ord.push(s[k].ts);
    ord.sort(function (a, b) { return a - b; });
    this.order = ord; this.oi = 0; this.held = []; this.reordered = 0;
    this.reorderOn = reorderNeeded();
    this._open(null);
}
/* A decoded frame joins the frames waiting for their turn (see REORDER_SLACK). */
DecodeStream.prototype._accept = function (f) {
    var h = this.held, ts = f.timestamp, i = h.length;
    if (i && h[i - 1].timestamp > ts) this.reordered++;
    while (i > 0 && h[i - 1].timestamp > ts) i--;
    h.splice(i, 0, f);
    this._release(false);
};
DecodeStream.prototype._release = function (all) {
    var h = this.held, o = this.order;
    while (h.length) {
        var next = this.oi < o.length ? o[this.oi] : Infinity;
        if (!all && this.reorderOn && h[0].timestamp > next && h.length <= REORDER_SLACK) break;
        var f = h.shift();
        while (this.oi < o.length && o[this.oi] <= f.timestamp) this.oi++;
        // `lastTs` is the newest frame GIVEN OUT: a rescue restarts from it and
        // drops what is not newer — a frame still waiting here is not «given»
        if (f.timestamp > this.lastTs) this.lastTs = f.timestamp;
        this.q.push(f);
    }
    this._wake();
};
/* One decoder per generation; anything a previous generation still delivers
   (a late frame, a flush, a read that was in flight) is ignored by `gen`. */
DecodeStream.prototype._open = function (hint) {
    var self = this, gen = ++this.gen;
    this._genAlive0 = this.alive || 0;   // what this decoder found; anything above it is its own output
    this.dec = new VideoDecoder({
        output: function (f) {
            // liveness counts every frame of THIS decoder, the dropped ones too:
            // a rescue re-decoding up to the last given-out frame is alive
            if (gen === self.gen) self.alive = (self.alive || 0) + 1;
            if (gen !== self.gen || f.timestamp <= self.dropUpTo) { try { f.close(); } catch (e) {} return; }
            self.nOut = (self.nOut || 0) + 1;
            self._accept(orientOut(f, self.rotationDeg));
        },
        error: function (e) { if (gen === self.gen) self._onError(e); }
    });
    this.gate = ('ondequeue' in this.dec) && typeof this.dec.addEventListener === 'function';
    /* `dequeue` refills the queue itself. Waking only a waiting next() left the
       decoder idle whenever the CONSUMER was busy (encoding the previous frame):
       decode and encode would take turns instead of running side by side. */
    if (this.gate) this.dec.addEventListener('dequeue', function () {
        if (gen !== self.gen || self.err || self.closed) return;
        self._feed(); self._wake();
    });
    var cfg = decoderConfig(this.d);
    if (hint) cfg.hardwareAcceleration = hint;
    this.dec.configure(cfg);
};
// Room left in the decoder's own input queue; Infinity when it cannot tell us (no gate).
DecodeStream.prototype._room = function () {
    if (!this.gate) return Infinity;
    var max = (window.__DS_QMAX > 0) ? window.__DS_QMAX : DECODE_QUEUE_MAX;   // стенд подбора глубины
    return max - (this.dec.decodeQueueSize || 0);
};
DecodeStream.prototype._onError = function (e) {
    if (this.closed || this.rescues >= DECODE_RESCUE_HINTS.length) { this.err = e; this._wake(); return; }
    // frames that arrived early but were never given out are decoded again
    for (var hx = 0; hx < this.held.length; hx++) { try { this.held[hx].close(); } catch (x) {} }
    this.held = [];
    var hint = DECODE_RESCUE_HINTS[this.rescues++], s = this.d.samples, restart = this.lo;
    if (this.lastTs > -Infinity) {
        for (var i = this.lo; i < this.hi && i < this.fed + 1; i++) if (s[i].type === 'key' && s[i].ts <= this.lastTs) restart = i;
    }
    this.dropUpTo = this.lastTs;
    try { this.dec.close(); } catch (x) {}
    // the fresh decoder starts cold too: its keyframe's leading pictures stay out
    this.lead = leadingAfter(s, restart, this.hi);
    this.fed = restart; this.flushed = false; this.done = false; this._loading = null; this.pend = [];
    try { this._open(hint); }
    catch (x) { this.err = e; this._wake(); return; }
    // the fresh decoder gets a full silence window of its own
    this._wAt = this._now(); this._wAlive = this.alive || 0;
    try {
        window.__ae && window.__ae('feature_use', { f: 'decode_stream_rescued', hint: hint, n: this.rescues,
            err: String((e && e.message) || e || '').slice(0, 80), codec: String(this.d.codec || '').slice(0, 20),
            atMs: this.lastTs > -Infinity ? Math.round(this.lastTs / 1000) : -1, out: this.nOut || 0, fed: this.fed, from: restart,
            lead: this.leadSkipped || 0 });
    } catch (x) {}
    this._wake();
};
DecodeStream.prototype._decode = function (chunk) {
    try { this.dec.decode(chunk); return true; }
    catch (e) { this._onError(e); return false; }   // a codec closed under us is the same event as its error callback
};
DecodeStream.prototype._wake = function () { var w = this.waiters; this.waiters = []; for (var i = 0; i < w.length; i++) w[i](); };
/* End-of-feed is reached from both the eager and the lazy path, so it lives in
   one place — a second copy is how "flushed twice" and "never flushed" get in. */
DecodeStream.prototype._maybeFlush = function () {
    if (this.fed >= this.hi && !this.flushed) {
        this.flushed = true; var self = this, gen = this.gen;
        var settled = function () {
            if (gen !== self.gen) return;
            /* A decoder that took every chunk, flushed, and never handed back a
               single frame did not decode this clip — the swallowing wedge that
               does settle its flush. «Done, nothing came out» would read as a
               clip without frames; it is the decoder's failure, rescued like
               one. */
            if ((self.alive || 0) === self._genAlive0 && self.hi > self.lo && !self.closed) {
                self._onError(new Error('decoder flushed without a frame'));
                return;
            }
            self._release(true);          // nothing more is coming: every waiting frame goes out
            self.done = true; self._wake();
        };
        this.dec.flush().then(settled).catch(settled);
    }
};
/* A lazy demux (demuxLazy) carries sample OFFSETS instead of sample bytes, so
   feeding the decoder becomes asynchronous. Reads are coalesced: samples are
   laid out consecutively in mdat, so a run of them is usually ONE slice rather
   than one per sample. The span is bounded because an interleaved file puts
   audio between the video samples — without the bound, "read the run" would
   quietly pull the whole mdat back into memory and undo the point of this.
   One read in flight at a time; completion re-enters through _wake(), the same
   path the decoder's own output uses, so next() needs no special case.
   Reading and handing to the decoder are SEPARATE steps: one window (≤ 8 MB,
   ≤ PREFETCH_MAX samples) is kept read ahead, and chunks go from it to the
   decoder while its queue has room. Reading only what the queue could take made
   every `dequeue` a one-sample Blob read — the same 1080p + 4K fade merge went
   from 22 s to 50 s. */
var LAZY_SPAN_BYTES = 8 * 1024 * 1024;
var PREFETCH_MAX = 32;
DecodeStream.prototype._feedLazy = function () {
    var s = this.d.samples, pend = this.pend || (this.pend = []);
    // 1. hand over what is already read — while the decoder has room and frames are not piling up
    while (pend.length && this.q.length < this.HWM && this._room() > 0) {
        var p = pend[0], x = s[p.i];
        if (this.lead[p.i]) { pend.shift(); this.fed = p.i + 1; this.leadSkipped++; continue; }
        // EncodedVideoChunk copies, so the window is free once its last sample is handed over.
        if (!this._decode(new EncodedVideoChunk({ type: x.type, timestamp: x.ts, duration: x.dur, data: p.u.subarray(p.a, p.a + x.size) }))) return;
        pend.shift(); this.fed = p.i + 1;
    }
    if (this.fed >= this.hi) { this._maybeFlush(); return; }
    // 2. keep one window read ahead
    if (this._loading || pend.length >= PREFETCH_MAX / 2) return;
    var start = pend.length ? pend[pend.length - 1].i + 1 : this.fed;
    if (start >= this.hi) return;
    var end = start + 1, lo = s[start].offset, hi = s[start].offset + s[start].size;
    while (end < this.hi && (end - start) + pend.length < PREFETCH_MAX) {
        var n = s[end];
        var nlo = Math.min(lo, n.offset), nhi = Math.max(hi, n.offset + n.size);
        if (nhi - nlo > LAZY_SPAN_BYTES) break;
        lo = nlo; hi = nhi; end++;
    }
    var self = this, gen = this.gen;
    this._loadingAt = this._now();
    this._loading = this.d.file.slice(lo, hi).arrayBuffer().then(function (ab) {
        if (gen !== self.gen) return;   // a rescue restarted the feed meanwhile
        var u = new Uint8Array(ab);
        for (var i = start; i < end; i++) pend.push({ i: i, u: u, a: s[i].offset - lo });
        self._loading = null;
        self._feedLazy(); self._wake();
    }).catch(function (e) {
        /* A read that fails must surface as a decode error, not as a stream that
           silently never produces another frame — next() would wait forever. */
        if (gen !== self.gen) return;
        self.err = e; self._loading = null; self._wake();
    });
};
DecodeStream.prototype._feed = function () {
    if (this.d.lazy) { this._feedLazy(); return; }
    while (this.q.length < this.HWM && this.fed < this.hi && this._room() > 0) {
        var li = this.fed, s = this.d.samples[this.fed++];
        if (this.lead[li]) { this.leadSkipped++; continue; }
        if (!this._decode(new EncodedVideoChunk({ type: s.type, timestamp: s.ts, duration: s.dur, data: s.data }))) return;
    }
    this._maybeFlush();
};
DecodeStream.prototype.next = function () {
    var self = this;
    return new Promise(function (resolve, reject) {
        (function attempt() {
            self._feed();
            if (self.err) { reject(self.err); return; }
            if (self.q.length) { resolve(self.q.shift()); return; }
            if (self.done) { resolve(null); return; }
            self.waiters.push(attempt);
            self._watch();
        })();
    });
};
DecodeStream.prototype._now = function () {
    return (typeof window.__visibleNow === 'function') ? window.__visibleNow()
        : ((window.performance && performance.now) ? performance.now() : Date.now());
};
/* The silence watchdog (see DECODE_SILENT_MS): armed by a waiting next(), gone
   with the last waiter. */
DecodeStream.prototype._watch = function () {
    if (this._wd || this.closed || !(this.silentMs > 0)) return;
    var self = this;
    this._wAt = this._now(); this._wAlive = this.alive || 0;
    this._wd = setInterval(function () {
        if (self.closed || self.err || !self.waiters.length) { clearInterval(self._wd); self._wd = 0; return; }
        var now = self._now(), alive = self.alive || 0;
        if (self._loading) {
            if (now - (self._loadingAt || now) > self.silentMs * 2) {
                self.err = new Error('decode-stream: file read did not answer in ' + Math.round(self.silentMs * 2 / 1000) + ' s');
                self.err.code = 'read-stalled';
                self._loading = null; self._wake();
            }
            self._wAt = now; self._wAlive = alive;
            return;
        }
        if (alive !== self._wAlive) { self._wAlive = alive; self._wAt = now; return; }
        if (now - self._wAt < self.silentMs) return;
        self._wAt = now;
        self.silences = (self.silences || 0) + 1;
        self._onError(new Error('decoder silent ' + Math.round(self.silentMs / 1000) + ' s'));
    }, 1000);
};
DecodeStream.prototype.close = function () {
    if (this._wd) { clearInterval(this._wd); this._wd = 0; }
    this.closed = true; this.gen++;   // nothing the decoder still delivers may start a rescue
    try { for (var i = 0; i < this.q.length; i++) this.q[i].close(); } catch (e) {}
    try { for (var j = 0; j < this.held.length; j++) this.held[j].close(); } catch (e) {}
    this.q = []; this.held = []; try { this.dec.close(); } catch (e) {}
};

/* Forward cursor: returns the source frame covering source-time tUs (holding
   the last frame at EOS), closing frames it walks past. Monotonic requests. */
function ClipCursor(stream, fps) {
    this.s = stream; this.cur = null; this.frameDurUs = 1e6 / fps;
    /* Chunks keep their raw cts (decoders are happier without negative
       timestamps), so the presentation shift is applied HERE — the one place
       a frame's time is compared against a caller's clock. `tUs` in, and every
       trim/seek the tools speak, is presentation time. See
       presentationOffsetUs for why raw cts is not that. */
    this.off = (stream && typeof stream.off === 'number') ? stream.off : 0;
}
/* `COVER_EPS_US` — a frame covers t only up to 2 µs before where the next one
   starts. Timestamps and the median frame length are both whole microseconds:
   at 24 fps a frame is 41 666.67 µs, rounded UP to 41 667, so «this frame's
   start + its length» overshot the next frame's start by 1 µs and a target
   landing exactly ON a frame boundary got the PREVIOUS picture. Measured
   2026-10-02 with a frame-number clip (frame-source-pulls-the-decoder.mjs R):
   24 fps sampled at 15, 8 targets of 60 one frame early; 29.97 fps rounds up
   the same way. 2 µs is nothing against a frame (≥ 4 000 µs at 250 fps). */
var COVER_EPS_US = 2;
/* Where the picture that starts at raw time `rawTs` stops being shown: the next
   presentation time in this stream's own sample table, not «start + one median
   frame». A phone that films in dim light, a screen recording or any edited
   clip changes its frame rate inside the file; with a fixed length the cursor
   walked onto the next picture while the current one was still on screen
   (#9567, 2026-10-16: a 17-fps-average clip whose frames come 20.7 and 15 a
   second — 109 of 581 targets got a picture from the future and 82 source
   frames were never handed out; dim-light 30→15 fps clip — 196 of 600).
   The last picture has no successor and keeps the median length. */
ClipCursor.prototype._endOf = function (rawTs) {
    var o = this.s && this.s.order;
    if (o && o.length) {
        var lo = 0, hi = o.length;
        while (lo < hi) { var m = (lo + hi) >> 1; if (o[m] <= rawTs) lo = m + 1; else hi = m; }
        if (lo < o.length) return o[lo];
    }
    return rawTs + this.frameDurUs;
};
ClipCursor.prototype.frameAt = async function (tUs) {
    var off = this.off;
    while (true) {
        if (this.cur && (this._endOf(this.cur.timestamp) - off) > tUs + COVER_EPS_US) return this._out();
        var nxt = await this.s.next();
        if (!nxt) return this._out();
        if (this.cur) this.cur.close();
        this.cur = nxt;
        if (this._endOf(this.cur.timestamp) - off > tUs + COVER_EPS_US) return this._out();
    }
};
/* Дверь кадра наружу. Только ОТДАВАЕМЫЙ кадр становится настоящим повёрнутым
   (копия — один раз: у копии поворот уже применён, и повторный вызов её
   не трогает); кадры, которые курсор пролистал, закрываются как были.
   Время копии — время исходника, поэтому _endOf работает по ней же. */
ClipCursor.prototype._out = function () {
    if (this.cur && !(this.s && this.s.deferRotation)) {
        var vo = (typeof window !== 'undefined') ? window.VideoOrientation : null;
        if (vo && vo.upright) this.cur = vo.upright(this.cur);
    }
    return this.cur;
};
ClipCursor.prototype.close = async function () { if (this.cur) { try { this.cur.close(); } catch (e) {} this.cur = null; } this.s.close(); };

/* ───────────────────────── LoopingClipCursor ─────────────────────────
   ClipCursor is monotonic-forward, so a clip that must REPEAT under a longer
   output (a 10 s background loop under a 227 s song) had no cursor at all —
   split-screen's Strategy B says so in writing and gates loopShort away from
   it. This is that missing piece, and it belongs beside ClipCursor rather than
   inside a tool because it is now the third caller of "give me this clip's
   frame at output time T": video-merge, split-screen, audio-to-video.

   Looping = a fresh DecodeStream per lap over the SAME demux result. That
   costs one VideoDecoder configure + a decode from the first keyframe — for a
   10 s clip under a 227 s song that is 23 rebuilds, against 5448 per-frame
   HTMLVideoElement seeks in the path this replaces (measured 2026-08-23:
   9.6 ms/frame decoder-pull vs 49–91 ms/frame seek-and-wait, and the seek path
   cannot be made frame-exact at all — see the readyState note in
   audio-to-video's drawBackground).

   `frameAt` returns a VideoFrame owned by the cursor: valid until the NEXT
   frameAt/close, exactly like ClipCursor. Draw it, don't keep it. */
function clipDurationUs(d) {
    var s = (d && d.samples) || [], end = 0;
    var off = (d && typeof d.ptsOffsetUs === 'number') ? d.ptsOffsetUs : 0;
    for (var i = 0; i < s.length; i++) { var e = s[i].ts - off + (s[i].dur > 0 ? s[i].dur : 0); if (e > end) end = e; }
    return end;
}
/* Median sample duration → source fps, the frame-cover denominator ClipCursor
   needs. Median (not mean) because a single long tail sample — common on
   phone-recorded clips — drags a mean far enough to skip frames. */
function sourceFpsOf(d) {
    var s = (d && d.samples) || [], durs = [];
    for (var i = 0; i < s.length && i < 90; i++) if (s[i].dur > 0) durs.push(s[i].dur);
    if (!durs.length) return 30;
    durs.sort(function (a, b) { return a - b; });
    var md = durs[durs.length >> 1];
    return md > 0 ? 1e6 / md : 30;
}
function LoopingClipCursor(d, fps, opts) {
    this.d = d;
    this.opts = opts || null;   // уходит в DecodeStream каждого круга (deferRotation)
    this.fps = fps > 0 ? fps : sourceFpsOf(d);
    this.clipUs = clipDurationUs(d);
    this.cur = null;
    this.lap = -1;
    this.lastInUs = -1;
}
LoopingClipCursor.prototype._rebuild = function () {
    var old = this.cur;
    this.cur = new ClipCursor(new DecodeStream(this.d, 0, null, this.opts), this.fps);
    this.lastInUs = -1;
    if (old) { try { old.close(); } catch (e) {} }
};
LoopingClipCursor.prototype.frameAt = async function (outUs) {
    if (!(this.clipUs > 0)) return null;
    var lap = Math.floor(outUs / this.clipUs);
    var inUs = outUs - lap * this.clipUs;
    /* A rebuild is needed on a new lap and also if the caller ever walks
       BACKWARDS inside one lap — the underlying cursor cannot rewind, and
       silently returning the held (later) frame would be the same
       "shows one thing, saves another" defect this replaces. */
    if (lap !== this.lap || inUs < this.lastInUs) { this._rebuild(); this.lap = lap; }
    this.lastInUs = inUs;
    return await this.cur.frameAt(inUs);
};
LoopingClipCursor.prototype.close = async function () {
    if (this.cur) { try { await this.cur.close(); } catch (e) {} this.cur = null; }
};

/* ───────── per-clip draw: rotate-before-scale, fit + center ─────────
   Mirrors buildClipVf: transpose → scale → pad/crop → setsar.
   `fit` — 'contain' (вписать, поля; так было всегда) или 'cover' (заполнить,
   лишнее обрезается холстом). Тот же выбор строит фильтровую цепочку
   медленного лейна (vmFitChain в блейде): если эти двое разойдутся, один и
   тот же проект даст разный кадр в зависимости от того, какой лейн выиграл, —
   и увидеть это можно будет только сравнив два готовых файла. */
function drawClipFrame(ctx, frame, rotation, W, H, fit) {
    ctx.save();
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H);
    /* `rotation` здесь — поворот, который выбрал ПОЛЬЗОВАТЕЛЬ пилюлями в UI.
       Поворот КОНТЕЙНЕРА уже сидит в кадре (DecodeStream), и складываются они
       естественно: контейнерный внутри VO.draw, пользовательский снаружи. */
    var _vo = VO();
    var _s = _vo ? _vo.size(frame) : { w: frame.displayWidth || frame.codedWidth, h: frame.displayHeight || frame.codedHeight };
    var sw = _s.w, sh = _s.h;
    // effective source dims after rotation
    var rw = (rotation === 90 || rotation === 270) ? sh : sw;
    var rh = (rotation === 90 || rotation === 270) ? sw : sh;
    var scale = (fit === 'cover') ? Math.max(W / rw, H / rh) : Math.min(W / rw, H / rh);
    var dw = rw * scale, dh = rh * scale;
    ctx.translate(W / 2, H / 2);
    if (rotation) ctx.rotate(rotation * Math.PI / 180);
    // after rotate, draw the source centered at its own (pre-rotation) scaled size
    var ddw = (rotation === 90 || rotation === 270) ? dh : dw;
    var ddh = (rotation === 90 || rotation === 270) ? dw : dh;
    if (_vo) _vo.draw(ctx, frame, -ddw / 2, -ddh / 2, ddw, ddh);
    else ctx.drawImage(frame, -ddw / 2, -ddh / 2, ddw, ddh);
    ctx.restore();
}

/* ───────── transition compositor ─────────
   prog 0→1 across the overlap. A = outgoing clip, B = incoming.

   ЧТО ЗНАЧИТ КАЖДОЕ ИМЯ, РЕШАЕТ FFmpeg xfade (libavfilter/vf_xfade.c; у этих
   эффектов формулы выпусков n6.0 и n7.0 совпадают), а не мы. Эти имена — его имена:
   их же исполняет FFmpeg-граф склейки, ими же пользуются все, кто знает xfade.
   До 22.10.2026 здесь была своя карта на 18 имён, и она расходилась с ним:
   «wipeleft» рос слева (у xfade граница едет справа налево — /video-editor
   заметил это первым), «smooth*» были сдвигом, а не мягкой шторкой, остальные
   22 пункта меню («диагональ», «пикселизация», «нарезка», «сжатие»…) молча
   становились наплывом. Пикер обещал 40 эффектов, файл получал ~13 разных.
   За 30 дней RU — 75 склеек из 280 с переходом с подменой хотя бы одного.

   Как устроено:
     • xfadePixels — перенос формул xfade на RGBA построчно, с той же
       арифметикой float→uint8 (усечение), тем же порядком операций и float32
       там, где от него зависит порог (dissolve, pixelize). Сверка с настоящим
       FFmpeg по пикселям — tests/js/xfade-transitions-match-ffmpeg.test.mjs.
     • compositeTransition — рисует на холсте: где холст делает ровно то же
       самое (наплыв = globalAlpha, шторки и углы = прямоугольник отсечения по
       тем же целым границам, сдвиги = целые смещения), — средствами холста;
       остальное — через xfadePixels на своих холстах (кадры читаются оттуда,
       не с холста экспорта: чтение назад с него перевело бы весь экспорт на
       процессор).
   ⚠️ Контракт колбэков: drawA(g) / drawB(g) рисуют кадр на ПЕРЕДАННЫЙ контекст
   g. Колбэк, рисующий на «свой» холст и игнорирующий g, отдаст пиксельным
   эффектам пустой кадр. */
var XFADE_IDS = ['fade', 'fadeblack', 'fadewhite', 'fadegrays',
    'wipeleft', 'wiperight', 'wipeup', 'wipedown', 'wipetl', 'wipetr', 'wipebl', 'wipebr',
    'slideleft', 'slideright', 'slideup', 'slidedown',
    'smoothleft', 'smoothright', 'smoothup', 'smoothdown',
    'circlecrop', 'circleopen', 'circleclose', 'radial',
    'vertopen', 'vertclose', 'horzopen', 'horzclose',
    'diagtl', 'diagtr', 'diagbl', 'diagbr',
    'hlslice', 'hrslice', 'vuslice', 'vdslice',
    'dissolve', 'pixelize', 'hblur', 'zoomin', 'squeezeh', 'squeezev'];
var KNOWN = {};
XFADE_IDS.forEach(function (k) { KNOWN[k] = true; });

var F32 = Math.fround;
function xfSmooth(e0, e1, x) {
    var t = (x - e0) / (e1 - e0);
    t = t < 0 ? 0 : (t > 1 ? 1 : t);
    return t * t * (3 - 2 * t);
}
/* то же в float32 — там, где результат сравнивается с порогом (нарезки) */
function xfSmooth32(e0, e1, x) {
    var t = F32(F32(x - e0) / F32(e1 - e0));
    t = t < 0 ? 0 : (t > 1 ? 1 : t);
    return F32(F32(t * t) * F32(3 - F32(2 * t)));
}
function xfFract(a) { return F32(a - Math.floor(a)); }
/* frand() xfade — в float32, как в C: от него зависит, какой пиксель уже
   «растворился»; в double доля таких пикселей расходилась бы на проценты. */
var XF_K1 = F32(12.9898), XF_K2 = F32(78.233), XF_K3 = F32(43758.545);
function xfRand(x, y) {
    var r = F32(F32(Math.sin(F32(F32(x * XF_K1) + F32(y * XF_K2)))) * XF_K3);
    return F32(r - Math.floor(r));
}
/* lrintf: к ближайшему, половина — к чётному. */
function xfRint(v) {
    var f = Math.floor(v), d = v - f;
    if (d > 0.5) return f + 1;
    if (d < 0.5) return f;
    return (f % 2 === 0) ? f : f + 1;
}

/* A, B, O — RGBA (Uint8ClampedArray / Uint8Array) W×H; P — progress xfade:
   1 в начале перехода, 0 в конце (prog = 1 − P). Альфа выхода — 255.

   Скорость: формулы те же, но (1) всё, что от P не зависит (угол, расстояние,
   шум, произведение координат), считается один раз на размер кадра; (2) кадр
   сначала превращается в массив «доли нового кадра» (wt), а смешивание — один
   проход без вызова функции на пиксель, с целыми копиями там, где доля 0 или 1.
   Замер 22.10.2026 (Chromium, 1920×1080): прямой перенос — 30–100 мс на кадр,
   этот — см. разбор в docs/why/video-merge-transitions.md. */
var XF_ALPHA = 0xff000000;   // старший байт uint32 — альфа (little-endian)
function xfU32(a) { return new Uint32Array(a.buffer, a.byteOffset, a.length >> 2); }
var _xfMask = null, _xfWt = null;
/* Не зависящая от P часть формулы — на размер и тип. */
function xfMask(type, W, H) {
    var key = type + ':' + W + 'x' + H;
    if (_xfMask && _xfMask.key === key) return _xfMask.m;
    var m = new Float32Array(W * H), hw = (W / 2) | 0, hh = (H / 2) | 0, x, y, p = 0;
    var zc = Math.hypot(hw, hh);
    for (y = 0; y < H; y++) for (x = 0; x < W; x++, p++) {
        switch (type) {
        case 'radial':      m[p] = Math.atan2(x - hw, y - hh); break;
        case 'circleopen': case 'circleclose': m[p] = Math.hypot(x - hw, y - hh) / zc; break;
        case 'circlecrop':  m[p] = Math.hypot(x - hw, y - hh); break;
        case 'diagtl':      m[p] = x / W * y / H; break;
        case 'diagtr':      m[p] = (W - 1 - x) / W * y / H; break;
        case 'diagbl':      m[p] = x / W * (H - 1 - y) / H; break;
        case 'diagbr':      m[p] = (W - 1 - x) / W * (H - 1 - y) / H; break;
        case 'dissolve':    m[p] = F32(xfRand(x, y) * 2); break;
        }
    }
    _xfMask = { key: key, m: m };
    return m;
}
function xfWeights(N) {
    if (!_xfWt || _xfWt.length !== N) _xfWt = new Float32Array(N);
    return _xfWt;
}
/* O = B·s + A·(1−s), s = wt[p] — доля нового кадра. */
function xfMixWeights(A, B, O, wt, N) {
    var A32 = xfU32(A), B32 = xfU32(B), O32 = xfU32(O), s, r, i, p;
    for (p = 0, i = 0; p < N; p++, i += 4) {
        s = wt[p];
        if (s <= 0) O32[p] = A32[p] | XF_ALPHA;
        else if (s >= 1) O32[p] = B32[p] | XF_ALPHA;
        else {
            r = 1 - s;
            O32[p] = ((B[i] * s + A[i] * r) | 0) | (((B[i + 1] * s + A[i + 1] * r) | 0) << 8)
                | (((B[i + 2] * s + A[i + 2] * r) | 0) << 16) | XF_ALPHA;
        }
    }
}
/* Доля нового кадра по столбцам / по строкам — разложить на весь кадр. */
function xfFillCols(wt, col, W, H) { for (var y = 0, p = 0; y < H; y++) for (var x = 0; x < W; x++, p++) wt[p] = col[x]; }
function xfFillRows(wt, row, W, H) { for (var y = 0, p = 0; y < H; y++) { var v = row[y]; for (var x = 0; x < W; x++, p++) wt[p] = v; } }

function xfadePixels(type, A, B, O, W, H, P) {
    P = F32(P < 0 ? 0 : (P > 1 ? 1 : P));
    var N = W * H, x, y, i, j, p, s, m, z;
    var hw = (W / 2) | 0, hh = (H / 2) | 0;
    var A32 = xfU32(A), B32 = xfU32(B), O32 = xfU32(O);
    var wt, col, row, mk;
    switch (type) {
    case 'wipeleft': case 'wiperight': case 'wipeup': case 'wipedown':
    case 'wipetl': case 'wipetr': case 'wipebl': case 'wipebr': {
        var r = xfWipeRectA(type, W, H, P);
        for (y = 0, p = 0; y < H; y++) {
            var rowA = y >= r[1] && y < r[3];
            for (x = 0; x < W; x++, p++) O32[p] = ((rowA && x >= r[0] && x < r[2]) ? A32[p] : B32[p]) | XF_ALPHA;
        }
        return;
    }
    case 'slideleft': case 'slideright':
        z = type === 'slideleft' ? Math.trunc(-P * W) : Math.trunc(P * W);
        for (y = 0, p = 0; y < H; y++) {
            var base = y * W;
            for (x = 0; x < W; x++, p++) {
                var zx = z + x, zz = zx % W + W * (zx < 0 ? 1 : 0);
                if (zz >= W) zz -= W;
                O32[p] = ((zx >= 0 && zx < W) ? B32[base + zz] : A32[base + zz]) | XF_ALPHA;
            }
        }
        return;
    case 'slideup': case 'slidedown':
        z = type === 'slideup' ? Math.trunc(-P * H) : Math.trunc(P * H);
        for (y = 0, p = 0; y < H; y++) {
            var zy = z + y, zr = zy % H + H * (zy < 0 ? 1 : 0);
            if (zr >= H) zr -= H;
            var src = (zy >= 0 && zy < H) ? B32 : A32, sb = zr * W;
            for (x = 0; x < W; x++, p++) O32[p] = src[sb + x] | XF_ALPHA;
        }
        return;
    case 'circlecrop':
        mk = xfMask(type, W, H);
        z = Math.pow(2 * Math.abs(P - 0.5), 3) * Math.hypot(hw, hh);
        var keep = P < 0.5 ? B32 : A32;
        for (p = 0; p < N; p++) O32[p] = z < mk[p] ? XF_ALPHA : (keep[p] | XF_ALPHA);
        return;
    case 'fadeblack': case 'fadewhite': case 'fadegrays': {
        /* mix(mix(a, g0, s1), mix(g1, b, s2), P), разложенное по слагаемым */
        var s1 = xfSmooth(0.8, 1, P), s2 = xfSmooth(0.2, 1, P);
        var ka = s1 * P, kb = (1 - s2) * (1 - P), kg0 = (1 - s1) * P, kg1 = s2 * (1 - P);
        if (type !== 'fadegrays') {
            var kc = (type === 'fadewhite' ? 255 : 0) * (kg0 + kg1);
            for (i = 0; i < N * 4; i += 4) {
                O[i] = (A[i] * ka + B[i] * kb + kc) | 0;
                O[i + 1] = (A[i + 1] * ka + B[i + 1] * kb + kc) | 0;
                O[i + 2] = (A[i + 2] * ka + B[i + 2] * kb + kc) | 0;
                O[i + 3] = 255;
            }
        } else {
            for (i = 0; i < N * 4; i += 4) {
                var g = (((A[i] + A[i + 1] + A[i + 2]) / 3) | 0) * kg0 + (((B[i] + B[i + 1] + B[i + 2]) / 3) | 0) * kg1;
                O[i] = (A[i] * ka + B[i] * kb + g) | 0;
                O[i + 1] = (A[i + 1] * ka + B[i + 1] * kb + g) | 0;
                O[i + 2] = (A[i + 2] * ka + B[i + 2] * kb + g) | 0;
                O[i + 3] = 255;
            }
        }
        return;
    }
    case 'radial':
        mk = xfMask(type, W, H); wt = xfWeights(N);
        z = (P - 0.5) * (Math.PI * 2.5);
        for (p = 0; p < N; p++) wt[p] = xfSmooth(0, 1, mk[p] - z);
        xfMixWeights(A, B, O, wt, N);
        return;
    case 'circleopen': case 'circleclose': {
        mk = xfMask(type, W, H); wt = xfWeights(N);
        var pc = ((type === 'circleopen' ? P : 1 - P) - 0.5) * 3, open = type === 'circleopen';
        /* circleopen: mix(a, b, s) — наоборот относительно остальных */
        for (p = 0; p < N; p++) { s = xfSmooth(0, 1, mk[p] + pc); wt[p] = open ? 1 - s : s; }
        xfMixWeights(A, B, O, wt, N);
        return;
    }
    case 'diagtl': case 'diagtr': case 'diagbl': case 'diagbr':
        mk = xfMask(type, W, H); wt = xfWeights(N);
        z = 1 - P * 2;
        for (p = 0; p < N; p++) wt[p] = xfSmooth(0, 1, mk[p] + z);
        xfMixWeights(A, B, O, wt, N);
        return;
    case 'smoothleft': case 'smoothright': case 'vertopen': case 'vertclose':
    case 'hlslice': case 'hrslice':
        col = new Float32Array(W);
        for (x = 0; x < W; x++) {
            if (type === 'smoothleft' || type === 'smoothright') {
                col[x] = xfSmooth(0, 1, 1 + (type === 'smoothleft' ? x : (W - 1 - x)) / W - P * 2);
            } else if (type === 'vertopen' || type === 'vertclose') {
                /* целая середина, как в выпусках n6.0 / n7.0 (наш WASM и сервер); ветка
                   release/7.0 позже перешла на width / 2.0 */
                m = Math.abs((x - hw) / hw);
                col[x] = xfSmooth(0, 1, (type === 'vertopen' ? 2 - m : 1 + m) - P * 2);
            } else {
                /* hlslice: fract(10·x/w), hrslice: fract(10·xx) — порядок операций как в C */
                var xx = type === 'hlslice' ? F32(x / W) : F32((W - 1 - x) / W);
                var frx = type === 'hlslice' ? xfFract(F32(F32(10 * x) / W)) : xfFract(F32(10 * xx));
                col[x] = xfSmooth32(-0.5, 0, F32(xx - F32(P * 1.5))) <= frx ? 0 : 1;
            }
        }
        wt = xfWeights(N); xfFillCols(wt, col, W, H); xfMixWeights(A, B, O, wt, N);
        return;
    case 'smoothup': case 'smoothdown': case 'horzopen': case 'horzclose':
    case 'vuslice': case 'vdslice':
        row = new Float32Array(H);
        for (y = 0; y < H; y++) {
            if (type === 'smoothup' || type === 'smoothdown') {
                row[y] = xfSmooth(0, 1, 1 + (type === 'smoothup' ? y : (H - 1 - y)) / H - P * 2);
            } else if (type === 'horzopen' || type === 'horzclose') {
                m = Math.abs((y - hh) / hh);
                row[y] = xfSmooth(0, 1, (type === 'horzopen' ? 2 - m : 1 + m) - P * 2);
            } else {
                var yy = type === 'vuslice' ? F32(y / H) : F32((H - 1 - y) / H);
                var fry = type === 'vuslice' ? xfFract(F32(F32(10 * y) / H)) : xfFract(F32(10 * yy));
                row[y] = xfSmooth32(-0.5, 0, F32(yy - F32(P * 1.5))) <= fry ? 0 : 1;
            }
        }
        wt = xfWeights(N); xfFillRows(wt, row, W, H); xfMixWeights(A, B, O, wt, N);
        return;
    case 'dissolve': {
        mk = xfMask(type, W, H);
        var p2 = F32(P * 2);
        for (p = 0; p < N; p++) O32[p] = (F32(F32(mk[p] + p2) - 1.5) >= 0.5 ? A32[p] : B32[p]) | XF_ALPHA;
        return;
    }
    case 'pixelize': {
        var d = F32(Math.min(P, F32(1 - P)));
        var dist = F32(Math.ceil(F32(d * 50)) / 50);
        var sq = F32(F32(F32(2 * dist) * Math.min(W, H)) / 20);
        var sxs = new Int32Array(W);
        for (x = 0; x < W; x++) sxs[x] = dist > 0 ? Math.trunc(Math.min(F32((Math.floor(F32(x / sq)) + 0.5) * sq), W - 1)) : x;
        var q = 1 - P;
        for (y = 0, i = 0; y < H; y++) {
            var sy = dist > 0 ? Math.trunc(Math.min(F32((Math.floor(F32(y / sq)) + 0.5) * sq), H - 1)) : y;
            for (x = 0; x < W; x++, i += 4) {
                j = (sy * W + sxs[x]) * 4;
                O[i] = (A[j] * P + B[j] * q) | 0;
                O[i + 1] = (A[j + 1] * P + B[j + 1] * q) | 0;
                O[i + 2] = (A[j + 2] * P + B[j + 2] * q) | 0;
                O[i + 3] = 255;
            }
        }
        return;
    }
    case 'hblur': {
        /* скользящее окно по строке, три канала за проход */
        var pr = P <= 0.5 ? P * 2 : (1 - P) * 2;
        var size = Math.trunc(1 + hw * pr), qq = 1 - P;
        for (y = 0, p = 0; y < H; y++) {
            var rb = y * W * 4, a0 = 0, a1 = 0, a2 = 0, b0 = 0, b1 = 0, b2 = 0, cnt = size;
            for (x = 0; x < size && x < W; x++) {
                j = rb + x * 4;
                a0 += A[j]; a1 += A[j + 1]; a2 += A[j + 2]; b0 += B[j]; b1 += B[j + 1]; b2 += B[j + 2];
            }
            var ka = P / cnt, kb = qq / cnt;   // меняются, только когда окно упирается в край
            for (x = 0; x < W; x++, p++) {
                var at = rb + x * 4;
                O32[p] = ((a0 * ka + b0 * kb) | 0) | (((a1 * ka + b1 * kb) | 0) << 8)
                    | (((a2 * ka + b2 * kb) | 0) << 16) | XF_ALPHA;
                if (x + size < W) {
                    j = at + size * 4;
                    a0 += A[j] - A[at]; a1 += A[j + 1] - A[at + 1]; a2 += A[j + 2] - A[at + 2];
                    b0 += B[j] - B[at]; b1 += B[j + 1] - B[at + 1]; b2 += B[j + 2] - B[at + 2];
                } else {
                    a0 -= A[at]; a1 -= A[at + 1]; a2 -= A[at + 2];
                    b0 -= B[at]; b1 -= B[at + 1]; b2 -= B[at + 2]; cnt--;
                    ka = P / cnt; kb = qq / cnt;
                }
            }
        }
        return;
    }
    case 'squeezeh':
        for (y = 0, p = 0; y < H; y++) {
            z = F32(0.5 + F32(F32(F32(y / H) - 0.5) / P));
            var outH = !(z >= 0 && z <= 1), rbase = (outH ? y : xfRint(F32(z * (H - 1)))) * W, srcH = outH ? B32 : A32;
            for (x = 0; x < W; x++, p++) O32[p] = srcH[rbase + x] | XF_ALPHA;
        }
        return;
    case 'squeezev': {
        var cx = new Int32Array(W);   // −1 — столбец нового кадра
        for (x = 0; x < W; x++) {
            z = F32(0.5 + F32(F32(F32(x / W) - 0.5) / P));
            cx[x] = (z >= 0 && z <= 1) ? xfRint(F32(z * (W - 1))) : -1;
        }
        for (y = 0, p = 0; y < H; y++) {
            var bse = y * W;
            for (x = 0; x < W; x++, p++) O32[p] = (cx[x] < 0 ? B32[p] : A32[bse + cx[x]]) | XF_ALPHA;
        }
        return;
    }
    case 'zoomin': {
        var zf = xfSmooth32(0.5, 1, P), zm = xfSmooth(0, 0.5, P), zr = 1 - zm;
        var ius = new Int32Array(W);
        for (x = 0; x < W; x++) ius[x] = Math.ceil(F32(F32(0.5 + F32(F32(F32(x / W) - 0.5) * zf)) * (W - 1)));
        for (y = 0, i = 0; y < H; y++) {
            var ivb = Math.ceil(F32(F32(0.5 + F32(F32(F32(y / H) - 0.5) * zf)) * (H - 1))) * W;
            for (x = 0; x < W; x++, i += 4) {
                j = (ivb + ius[x]) * 4;
                O[i] = (A[j] * zm + B[i] * zr) | 0;
                O[i + 1] = (A[j + 1] * zm + B[i + 1] * zr) | 0;
                O[i + 2] = (A[j + 2] * zm + B[i + 2] * zr) | 0;
                O[i + 3] = 255;
            }
        }
        return;
    }
    default: {   // fade
        var qf = 1 - P;
        for (p = 0, i = 0; p < N; p++, i += 4) {
            O32[p] = ((A[i] * P + B[i] * qf) | 0) | (((A[i + 1] * P + B[i + 1] * qf) | 0) << 8)
                | (((A[i + 2] * P + B[i + 2] * qf) | 0) << 16) | XF_ALPHA;
        }
    }
    }
}

/* Шторки xfade: целые границы, где ещё виден уходящий кадр, — одни и те же у
   пиксельного пути и у прямоугольника отсечения холста. */
function xfWipeBounds(type, W, H, P) {
    switch (type) {
    case 'wipeleft':  return { zw: Math.trunc(W * P) };
    case 'wiperight': return { zw: Math.trunc(W * (1 - P)) };
    case 'wipeup':    return { zh: Math.trunc(H * P) };
    case 'wipedown':  return { zh: Math.trunc(H * (1 - P)) };
    case 'wipetl':    return { zw: Math.trunc(W * P), zh: Math.trunc(H * P) };
    case 'wipetr':    return { zw: Math.trunc(W * (1 - P)), zh: Math.trunc(H * P) };
    case 'wipebl':    return { zw: Math.trunc(W * P), zh: Math.trunc(H * (1 - P)) };
    default:          return { zw: Math.trunc(W * (1 - P)), zh: Math.trunc(H * (1 - P)) };
    }
}
/* Прямоугольник, где виден УХОДЯЩИЙ кадр (A) у шторки: [x0, x1) × [y0, y1). */
function xfWipeRectA(type, W, H, P) {
    var b = xfWipeBounds(type, W, H, P), cl = function (v, hi) { return v < 0 ? 0 : (v > hi ? hi : v); };
    var lx = cl(b.zw + 1, W), ly = cl(b.zh + 1, H);
    switch (type) {
    case 'wipeleft':  return [0, 0, lx, H];
    case 'wiperight': return [lx, 0, W, H];
    case 'wipeup':    return [0, 0, W, ly];
    case 'wipedown':  return [0, ly, W, H];
    case 'wipetl':    return [0, 0, lx, ly];
    case 'wipetr':    return [lx, 0, W, ly];
    case 'wipebl':    return [0, ly, lx, H];
    default:          return [lx, ly, W, H];
    }
}

/* Холст для наплыва: входящий кадр собирается ЦЕЛИКОМ (со своей чёрной заливкой
   под кадром) и только потом ложится поверх под globalAlpha. Колбэк кадра сначала
   заливает холст чёрным (drawClipFrame) — нарисованный прямо в кадр под
   globalAlpha, он затемнял и уходящий кадр: посередине наплыва оставалось
   0,25·A + 0,5·B (стенд video-merge-transitions-match-xfade: MAE 19–27 против шума
   сжатия 4,6; /video-editor заметил это первым и собирал кадры отдельно). Холст
   «обычный» (GPU): наплыв не читает пиксели. */
var _xfLayer = null;
function xfLayer(W, H) {
    if (_xfLayer && _xfLayer.W === W && _xfLayer.H === H) return _xfLayer.g;
    var c = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(W, H) : document.createElement('canvas');
    c.width = W; c.height = H;
    _xfLayer = { W: W, H: H, c: c, g: c.getContext('2d') };
    return _xfLayer.g;
}

/* Холсты пиксельного пути: свои, «часто читаемые» — чтение назад не трогает
   холст экспорта. Один набор на размер. */
var _xfScratch = null;
function xfScratch(W, H) {
    if (_xfScratch && _xfScratch.W === W && _xfScratch.H === H) return _xfScratch;
    function mk() {
        var c = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(W, H) : document.createElement('canvas');
        c.width = W; c.height = H;
        return c.getContext('2d', { willReadFrequently: true });
    }
    var ga = mk(), gb = mk();
    _xfScratch = { W: W, H: H, ga: ga, gb: gb, out: ga.createImageData(W, H) };
    return _xfScratch;
}

function compositeTransition(ctx, drawA, drawB, prog, type, W, H) {
    type = String(type || 'fade').toLowerCase();
    var used = KNOWN[type] ? type : 'fade';
    prog = prog < 0 ? 0 : (prog > 1 ? 1 : prog);
    var P = 1 - prog;
    if (used === 'fade') {
        /* mix(a, b, P) = a·P + b·(1−P) — ровно готовый кадр B под globalAlpha = prog поверх A */
        var gl = xfLayer(W, H);
        gl.clearRect(0, 0, W, H);   // кадр, не покрывший холст, не должен показать прошлый
        drawB(gl);
        drawA(ctx);
        ctx.save(); ctx.globalAlpha = prog; ctx.drawImage(gl.canvas, 0, 0); ctx.restore();
        return used === type ? 'fade' : 'fade*' + type;
    }
    if (used.indexOf('wipe') === 0) {
        var r = xfWipeRectA(used, W, H, P);
        drawB(ctx);
        if (r[2] > r[0] && r[3] > r[1]) {
            ctx.save(); ctx.beginPath(); ctx.rect(r[0], r[1], r[2] - r[0], r[3] - r[1]); ctx.clip();
            drawA(ctx); ctx.restore();
        }
        return used;
    }
    if (used.indexOf('slide') === 0) {
        /* Оба кадра едут вместе на целое число пикселей — как у xfade. */
        var horiz = used === 'slideleft' || used === 'slideright';
        var N = horiz ? W : H;
        var z = (used === 'slideleft' || used === 'slideup') ? Math.trunc(-P * N) : Math.trunc(P * N);
        var offB = -z, offA = z < 0 ? -z - N : N - z;
        ctx.save(); ctx.translate(horiz ? offA : 0, horiz ? 0 : offA); drawA(ctx); ctx.restore();
        ctx.save(); ctx.translate(horiz ? offB : 0, horiz ? 0 : offB); drawB(ctx); ctx.restore();
        return used;
    }
    var S = xfScratch(W, H);
    /* холсты общие на все кадры: кадр, не покрывший их целиком, без очистки показал
       бы по краям прошлый */
    S.ga.clearRect(0, 0, W, H); drawA(S.ga); var ia = S.ga.getImageData(0, 0, W, H);
    S.gb.clearRect(0, 0, W, H); drawB(S.gb); var ib = S.gb.getImageData(0, 0, W, H);
    xfadePixels(used, ia.data, ib.data, S.out.data, W, H, P);
    ctx.putImageData(S.out, 0, 0);
    return used;
}

/* ───────── растяжение по времени для правки СКОРОСТИ ─────────
   Скорость раньше выбрасывала склейку в FFmpeg-WASM целиком (жалоба #1412:
   179 с на 13-секундный ролик; по телеметрии этот лейн даёт 189 с в среднем
   против 8 с у быстрого, и правка скорости — причина №1: 97 случаев за 14 дней).
   Видео-часть скорости здесь ничего не стоит — времянка уже делит длительность
   на speed, нужен лишь множитель при выборке кадра. Упиралось всё в ЗВУК: менять
   темп надо БЕЗ сдвига тона, иначе получится «бурундук» — тихая потеря качества.
   Своего DSP не пишем: одна дверь сайта — AudioPipeline.stretch, и движок она
   выбирает по СОДЕРЖИМОМУ клипа: речь → WSOLA, музыка → фазовый вокодер
   (30.09.2026: на речи вокодер делает голос полым — UTMOS −0.86 против −0.11
   у WSOLA на 1.2×, чат Премиума 661; до этого здесь был PitchEngine напрямую —
   вокодер для любого звука). Дверь грузится версионированно (ensureAudioPipeline);
   прежний загрузчик брал голый /js/pitch-engine.js, который статика держит в
   кэше год.
   ⚠️ Если дверь не поднялась (RU-сеть, старый браузер) — НЕ подменяем её
   playbackRate'ом: это как раз и был бы сдвиг тона. Честно уходим в FFmpeg. */
function ensureStretch() {
    if (window.AudioPipeline && typeof window.AudioPipeline.stretch === 'function') return Promise.resolve(true);
    if (typeof window.ensureAudioPipeline !== 'function') return Promise.resolve(false);
    return window.ensureAudioPipeline().then(
        function (ok) { return !!(ok && window.AudioPipeline && window.AudioPipeline.stretch); },
        function () { return false; });
}
function anySpeedEdit(clips) {
    for (var i = 0; i < clips.length; i++) if ((clips[i].speed || 1) !== 1) return true;
    return false;
}
/* Растягиваем ВЕСЬ буфер клипа один раз; после этого его временная шкала — уже
   выходная, поэтому trimStart при планировании делится на speed. */
async function stretchClipAudio(buf, speed, slug) {
    if (!(await ensureStretch())) throw new Error('stretch door unavailable');
    return await window.AudioPipeline.stretch(buf, { rate: speed, semitones: 0, slug: slug || 'video-merge' });
}

/* The timeline's length in seconds as merge() will lay it out: the caller's
   layout when it gave one, else trims ÷ speed minus the transitions' overlaps. */
function timelineSeconds(clips, opts) {
    var LO = opts && opts.layout;
    if (LO && LO.starts && LO.effDur && LO.starts.length) {
        var n = LO.starts.length - 1;
        return Math.max(0, (LO.starts[n] || 0) + (LO.effDur[n] || 0));
    }
    var t = 0;
    for (var i = 0; i < clips.length; i++) {
        var c = clips[i] || {}, len = ((c.trimEnd != null ? c.trimEnd : c.duration) || 0) - (c.trimStart || 0);
        t += Math.max(0, len) / (c.speed || 1);
    }
    return t;
}

/* ───────── eligibility probe ───────── */
async function probe(clips, opts) {
    opts = opts || {};
    clips = Array.prototype.slice.call(clips || []);   // та же фиксация ленты, что в merge()
    if (typeof VideoDecoder === 'undefined' || typeof VideoEncoder === 'undefined' ||
        typeof VideoFrame === 'undefined' || !window.CanvasVideoExport) {
        return { mode: null, reason: 'no-webcodecs' };
    }
    for (var i = 0; i < clips.length; i++) {
        if (clipIsImage(clips[i])) continue;              // фото не демуксится: его кадр — сама картинка
        var mkvOk = !!(opts.matroska && isMatroska(clips[i].file));
        if (!isMp4Family(clips[i].file) && !mkvOk) return { mode: null, reason: 'non-mp4-container', clip: i };
    }
    /* Скорость лейну больше не помеха — при условии, что поднимется движок
       растяжения. Проверяем ЗАРАНЕЕ: обещать быстрый лейн, а посреди работы
       обнаружить, что тон менять нечем, нельзя (см. «дорожку обещал декодер,
       решает энкодер»). Не поднялся — прежнее поведение, FFmpeg. */
    if (anySpeedEdit(clips) && !(await ensureStretch())) {
        return { mode: null, reason: 'speed-edit-no-stretch' };
    }
    try { await loadMp4Box(); } catch (e) { return { mode: null, reason: 'no-mp4box' }; }
    // Decodability of each clip (codec + dims). METADATA-ONLY demux — no sample
    // extraction, so the probe never holds a clip's full sample buffer (and nothing
    // is cached onto clips). merge() demuxes each clip fresh, on demand.
    // A per-clip refusal names the clip (`clip: j`): the caller can lift THAT clip
    // (re-encode it alone into the part contract) and probe again, instead of
    // sending the whole timeline to the single-pass WASM graph because of one file.
    var target = opts.target;
    for (var j = 0; j < clips.length; j++) {
        if (clipIsImage(clips[j])) continue;
        var d;
        try { d = await demuxClip(clips[j].file, true, opts); } catch (e) { return { mode: null, reason: 'demux-failed', clip: j }; }
        if (!d.desc && /avc1|h264|hvc1|hev1/i.test(d.codec)) return { mode: null, reason: 'avcc-missing', clip: j };
        if (!hasUsableDims(d)) return { mode: null, reason: 'no-dims', clip: j };
        try {
            var sup = await VideoDecoder.isConfigSupported(decoderConfig(d));
            if (!sup || !sup.supported) return { mode: null, reason: 'decoder-unsupported:' + d.codec, clip: j, codec: d.codec };
        } catch (e) { return { mode: null, reason: 'decoder-probe-threw', clip: j }; }
        /* …and its yes earned by decoding a real keyframe (clipDecodeVerdict). A
           verified refusal names the clip and its codec: lifting an H.264 clip
           into H.264 again would be refused the same way. */
        var dv = await clipDecodeVerdict(d, opts.slug || 'video-merge');
        if (dv && !dv.ok && !dv.uncertain) {
            try { window.__ae && window.__ae('feature_use', { slug: opts.slug || 'video-merge', f: 'wc_decoder_refused', codec: String(d.codec || '').slice(0, 20), why: String(dv.reason || '').slice(0, 60) }); } catch (e) {}
            return { mode: null, reason: 'decoder-refused:' + d.codec, clip: j, codec: d.codec };
        }
    }
    // video encoder support at target
    var fmt = opts.format || 'mp4';
    // mov/mkv output containers are the FFmpeg engine's job (mp4-muxer writes
    // ISO-BMFF/WebM only) — bail BEFORE the encoder probe so the fallback
    // produces the container the user actually chose.
    if (fmt !== 'mp4' && fmt !== 'webm') return { mode: null, reason: 'container-choice:' + fmt };
    /* The encoder is judged on the export's own numbers — its crf (or the
       caller's bitrate) at the target rate — and, for a caller whose frames
       are drawn by time (merge() is: drawFrame reads tSec), the exporter may
       answer with a LOWER rate the device's own H.264 can make where the
       alternative is VP9 in the .mp4 (CanvasVideoExport.isSupported,
       allowFpsStep — comment #1990). A part stitched next to byte-copied clips
       (h264Only) keeps its rate: the concat needs it. */
    var vidOk = await window.CanvasVideoExport.isSupported({ format: fmt === 'webm' ? 'webm' : 'mp4',
        width: target.tw, height: target.th, fps: target.fps,
        qualityCrf: opts.crf != null ? opts.crf : 18, bitrate: opts.bitrate > 0 ? opts.bitrate : undefined,
        allowFpsStep: !opts.h264Only && fmt !== 'webm' });
    if (vidOk && vidOk.fpsStep && vidOk.fps > 0 && vidOk.fps !== target.fps) {
        target = Object.assign({}, target, { fps: vidOk.fps, fpsStep: vidOk.fpsStep });
    }
    // isSupported also gates on AAC → 'no-audio-encoder' on Firefox; that's the SPLIT trigger,
    // video itself is fine. Distinguish: if reason is exactly no-audio-encoder, video is ok.
    /* ⚠️⚠️ `ok` НЕ значит «кадр отсюда выйдет». Последняя ступень
       `pickVideoCodec` намеренно возвращает НЕПРОВЕРЕННЫЙ конфиг («попытка
       лучше капитуляции» — верно для зависшего GPU-процесса), и это `ok` с
       `proven:false`. Пока проверка стояла первой, каскад на VP9 ниже был
       МЁРТВЫМ КОДОМ ровно на тех машинах, ради которых написан: у браузера
       без лицензионного набора кодеков (≈12 % установок Яндекс.Браузера,
       36 сессий в неделю по телеметрии) H.264 не проверяется ничем, лейн
       брал работу, энкодер умирал на первых кадрах, и всё уезжало в
       однопроходный WASM-x264 — то есть человек платил временем ДВАЖДЫ.
       Признак различает два случая точно: зависший GPU проваливает ОБА
       зонда, а нехватка кодек-пака — только H.264, оставляя VP9 рабочим.
       Поэтому: проверенный mp4 → проверенный webm → непроверенная попытка.
       Стенд — «no-H.264 → VP9/WebM lane» в tests/playwright/video-merge.mjs
       (его симуляция до 2026-09-10 подменяла только совещательный
       `isConfigSupported`, который энкодер с 6a65b288eb уже не слушает). */
    if (vidOk.ok && (vidOk.proven !== false || fmt === 'webm')) {
        return { mode: 'fast', reason: 'ok', target: target, outFormat: fmt === 'webm' ? 'webm' : 'mp4' };
    }
    if (vidOk.ok) {
        var provenWebm = await window.CanvasVideoExport.isSupported({ format: 'webm',
            width: target.tw, height: target.th, fps: target.fps });
        if (provenWebm.ok && provenWebm.proven) {
            return { mode: 'fast', reason: 'ok-webm-proven', target: target, outFormat: 'webm' };
        }
        return { mode: 'fast', reason: 'ok-unproven', target: target, outFormat: 'mp4' };
    }
    if (vidOk.reason === 'no-audio-encoder') {
        /* No AAC encoder in this browser (Firefox, Chromium on Linux, older
           Yandex): the video is encoded here and the sound is glued on by
           FFmpeg — CanvasVideoExport.muxAudioInto, which holds the file in its
           memory up to remuxMaxBytes. A timeline that will not fit is told so
           now, not after the whole encode: WebM, whose Opus the browser
           encodes itself, when that is on offer. */
        var CVE = window.CanvasVideoExport, tot = timelineSeconds(clips, opts);
        var est = (CVE.estimateBytes && tot > 0) ? CVE.estimateBytes(target.tw, target.th, target.fps, opts.crf != null ? opts.crf : 18, tot, true) : 0;
        if (CVE.remuxMaxBytes > 0 && est > CVE.remuxMaxBytes) {
            var webmAv = await CVE.isSupported({ format: 'webm', width: target.tw, height: target.th, fps: target.fps, qualityCrf: opts.crf != null ? opts.crf : 18 });
            if (webmAv.ok) return { mode: 'fast', reason: 'split-too-large-webm', target: target, outFormat: 'webm' };
        }
        return { mode: 'split', reason: 'ok', target: target, outFormat: 'mp4' };
    }
    if (vidOk.reason === 'no-video-encoder' && fmt !== 'webm') {
        /* No H.264 encoder — the licensed-codec-pack gap. ~12% of YaBrowser installs
           ship WITHOUT proprietary codecs (H.264/AAC) but WITH royalty-free VP9+Opus
           (36 sessions/wk hit this dead-end per telemetry, and slideshow-maker
           confirmed the same population in c2b7ad223). Cascade to VP9+Opus → WebM:
           hardware-fast, bounded memory — the honest alternative to "no merge at all".
           The caller renames the download .webm and shows a localized notice. */
        var webmOk = await window.CanvasVideoExport.isSupported({ format: 'webm',
            width: target.tw, height: target.th, fps: target.fps });
        if (webmOk.ok) return { mode: 'fast', reason: 'ok-webm', target: target, outFormat: 'webm' };
        return { mode: null, reason: 'cve:no-video-encoder' };
    }
    return { mode: null, reason: 'cve:' + vidOk.reason };
}

/* ───────── audio mixdown — SEGMENT-CHUNKED OfflineAudioContext ─────────
   The old whole-timeline mixdown was O(total duration) heap: a 3-hour merge needs
   ~4 GB of decoded+rendered PCM, which is what killed the fokus33 report (9×8-min
   clips → "Array buffer allocation failed" before the encode even started).
   Chunked design: the timeline is rendered one CLIP SEGMENT at a time — segment k
   spans [starts[k], starts[k+1]) (last: …total]. A crossfade window lives at the
   START of segment k and mixes exactly clips k-1 and k, so a segment ever needs at
   most TWO decoded clips + its own rendered span. Decoded audio is cached only for
   {k-1, k} (each clip is decoded once, reused by the next segment's crossfade, then
   evicted). Heap = O(two largest clips), independent of clip count/total length.
   Returns { sampleRate, channels, next } for CanvasVideoExport's audioChunks input,
   or null when nothing on the timeline has audio. */
function makeAudioChunks(clips, transitions, starts, effDur, overlaps, opts, sr) {
    var n = clips.length;
    var total = starts[n - 1] + effDur[n - 1];
    var SLUG = opts.slug || 'video-merge';
    /* Свободные звуковые дорожки ленты (музыка, голос, шумы) — у каждой своё
       место на ленте, обрезка, повтор и огибающая громкости `env` в ВЫХОДНОМ
       времени. Огибающую считает вызывающий: по тем же числам звучит его
       превью, поэтому затухание в наушниках и в файле — одно. */
    var items = (opts.audioItems || []).filter(function (it) { return it && it.file && it.len > 0; });
    var anyAudio = clips.some(function (c) { return c.hasAudio && !c.mute; }) || (opts.bgMusic && opts.bgMusic.file) || items.length > 0;
    if (!anyAudio) return null;
    var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var decCtx = null;          // one small context reused for decodeAudioData (resamples to sr)
    var decCache = {};          // clipIndex -> AudioBuffer | null(failed/muted)   — WHOLE-clip decodes
    var bgmBuf, bgmTried = false;

    /* ── Звук клипа — ОКНАМИ, а не целиком (2026-09-10) ──────────────────────
       До этого `decodeAudioData((await file.arrayBuffer()).slice(0))` читал ВЕСЬ
       файл в кучу (дважды: буфер + копия) и держал PCM всего клипа: у 1.3 ГБ
       клипа на 15 минут — 2.6 ГБ транзитно + 345 МБ PCM; именно это держало
       потолок «1 ГиБ на файл» (RU 30 д: 142 сессии отбиты file_too_large) и
       рождало «Array buffer allocation failed» на длинных клипах (12 сессий,
       0 дошли). Видео этот лейн и так читает ленивыми срезами, а выдачу пишет в
       OPFS — звук был единственным, что требовало файл в памяти.
       Теперь клип длиннее окна или тяжелее 64 МБ (или тот, что браузер не
       декодирует — AC-3, AMR) идёт через потоковую дверь FFmpeg: `-ss a -t W`
       → WAV 48 кГц стерео (точный сэмпл-срез, декодер WASM, кодек любой) →
       decodeAudioData. Куча = O(окно): 300 с × 384 КБ/с = 115 МБ, а не
       O(клип). Короткие клипы и клипы со сменой темпа (растяжение с
       сохранением тона хочет весь буфер) — по-прежнему целиком.
       Буфер окна знает своё начало в секундах клипа (`_vmBase`); scheduleClip
       вычитает его из смещения. Стенд — tests/playwright/video-merge-audio-
       comes-in-windows.mjs (окно 3 с, сравнение с цельным декодом по сэмплам). */
    var WIN = (function () {
        if (window.__VM_AUDIO_WINDOW_SEC > 0) return window.__VM_AUDIO_WINDOW_SEC;   // стенд
        var gb = (window.__memBudget && typeof __memBudget.gb === 'function') ? __memBudget.gb() : 8;
        return gb <= 2 ? 120 : (gb <= 4 ? 180 : 300);
    })();
    var WINDOW_MIN_BYTES = 64 * 1048576;
    var WIN_TAIL = 0.02;        // с — хвост окна сверх звучания (см. getClipBuf)
    var winCache = {};          // clipIndex -> { a, b, buf }  — последнее окно клипа (в секундах КЛИПА)
    var winFailed = {};         // clipIndex -> true: окно не вышло, клип идёт целиком
    var winStats = { clips: 0, windows: 0, ms: 0, fails: 0 };
    function ffDoor() { return !!(window.FFmpegProcessor && typeof FFmpegProcessor.processStreaming === 'function'); }
    function rangeDoor() { return !!(window.AudioDecoderRobust && typeof window.AudioDecoderRobust.robustRange === 'function'); }
    function useWindows(j) {
        var c = clips[j];
        if (winFailed[j] || (!ffDoor() && !rangeDoor())) return false;
        if (Math.abs((c.speed || 1) - 1) > 0.001) return false;
        return ((c.file && c.file.size) || 0) > WINDOW_MIN_BYTES || effDur[j] > WIN;
    }
    function clipTime(j, tOut) { return (clips[j].trimStart || 0) + (tOut - starts[j]); }   // выходное время → секунды клипа (speed 1)
    /* Где клип j звучит внутри окна [w0, w1) выхода — ОДНО место на выборку
       окна звука, смещение в буфере и огибающую. Начало окна звука берётся
       от начала ЗВУЧАНИЯ (p0), а не от начала окна выхода: тогда смещение в
       только что выбранном окне — clipTime(p0) − clipTime(p0), ровно 0. */
    function spanIn(j, w0, w1) {
        var s = starts[j], p0 = Math.max(s, w0), p1 = Math.min(s + effDur[j], w1);
        return (p1 - p0 > 1e-6) ? { p0: p0, p1: p1 } : null;
    }

    /* A clip's sound through the ONE decode ladder (AudioDecoderRobust.robust:
       native → WebCodecs → FFmpeg → recovery), not a bare decodeAudioData.
       The bare call knows only the browser's WebAudio allow-list, so an AC-3 /
       ALAC / 32-bit-PCM clip — the probe above calls it «has audio» — came out
       SILENT in the merged file, with «done» on screen and no event at all.
       Measured 25.09.2026: [AAC 440 Hz][AC-3 660 Hz] with a fade — the AC-3
       clip's seconds at −137 dB / −inf. The raw call stays only for a page
       where the ladder did not load. */
    async function decodeWholeOnce(file) {
        var R = window.AudioDecoderRobust;
        if (R && typeof R.robust === 'function' && !R.__isStub) {
            var r = await R.robust(file, decCtx, { slug: SLUG });
            return (r && r.buffer) ? r.buffer : r;
        }
        return await decCtx.decodeAudioData((await file.arrayBuffer()).slice(0));
    }
    async function decodeClipWhole(j) {
        if (j in decCache) return decCache[j];
        var c = clips[j];
        if (!c.hasAudio || c.mute) { decCache[j] = null; return null; }
        try {
            if (!decCtx) decCtx = new OAC(2, sr, sr);
            var _buf = await decodeWholeOnce(c.file);
            var _sp = c.speed || 1;
            /* Темп меняем ТОЛЬКО через движок с сохранением тона. Он проверен в
               probe(), поэтому сюда мы попадаем лишь когда он поднялся. */
            if (Math.abs(_sp - 1) > 0.001) _buf = await stretchClipAudio(_buf, _sp, SLUG);
            decCache[j] = _buf;
        } catch (e) {
            // Still silent when every rung refused — but no longer unseen.
            decCache[j] = null;
            try {
                window.__ae && window.__ae('feature_use', {
                    slug: SLUG, f: 'wc_clip_audio_failed',
                    ext: String(((c.file && c.file.name) || '').split('.').pop() || '').toLowerCase().slice(0, 5),
                    err: String((e && e.message) || e || '').slice(0, 120),
                });
            } catch (_) {}
        }
        return decCache[j];
    }
    async function fetchWindow(j, a, b) {
        var c = clips[j], res = null, t0 = Date.now();
        if (!decCtx) decCtx = new OAC(2, sr, sr);
        /* ОДИН владелец окна на сайте (22.09.2026): AudioDecoderRobust.robustRange —
           окно прямо из контейнера через WebCodecs (AAC в mp4/mov с телефона, без
           30 МБ wasm), затем та же потоковая дверь FFmpeg `-ss a -t W`, что стояла
           здесь с 10.09, затем целиком под гейтом памяти. Буфер приходит на частоте
           ФАЙЛА — планировщик кладёт его в OfflineAudioContext, и тот
           пересэмплирует сам. Прежний код ниже остаётся запасным на случай, если
           модуль не поднялся. */
        if (rangeDoor()) {
            var r = await window.AudioDecoderRobust.robustRange(c.file, decCtx, { from: Math.max(0, a), to: b, slug: SLUG });
            var wb = r.buffer;
            wb._vmBase = a;
            var via = r.tier === 'wc-range' ? 'wc' : (r.tier === 'ff-range' ? 'ff' : 'whole');
            winStats[via] = (winStats[via] || 0) + 1;
            winStats.windows++; winStats.ms += Date.now() - t0;
            return wb;
        }
        try {
            // Рецепт точного окна — тот же, что у robustRange (audio-decoder.js):
            // вход — на целую секунду раньше, края — atrim счётом сэмплов.
            var a0 = Math.max(0, a), seekTo = Math.max(0, Math.floor(a0) - 1);
            var n0 = Math.round((a0 - seekTo) * sr), nw = Math.max(1, Math.round((b - a0) * sr));
            res = await FFmpegProcessor.processStreaming(c.file,
                ['-vn', '-map', '0:a:0', '-ac', '2', '-af', 'aresample=' + sr + ',atrim=start_sample=' + n0 + ':end_sample=' + (n0 + nw),
                    '-ar', String(sr), '-c:a', 'pcm_s16le'],
                'vmwc_aw.wav', undefined, (seekTo > 0 ? ['-ss', String(seekTo)] : []).concat(['-t', (b - seekTo + 0.5).toFixed(6)]));
            var buf = await decCtx.decodeAudioData(await res.blob.arrayBuffer());
            buf._vmBase = a;
            winStats.windows++; winStats.ms += Date.now() - t0;
            return buf;
        } finally { if (res && res.url) { try { URL.revokeObjectURL(res.url); } catch (_) {} } }
    }
    /* Буфер клипа j на его звучание sp = spanIn(…): целый клип или его окно.
       Прежнее окно годится, только если начато НЕ ПОЗЖЕ нужной секунды: окно,
       начатое позже (до 02.10 допускалось на 1 мс), дало бы смещение меньше
       нуля. */
    async function getClipBuf(j, sp) {
        var c = clips[j];
        if (!c.hasAudio || c.mute) return null;
        if (!useWindows(j)) return decodeClipWhole(j);
        /* Окно берётся с хвостом WIN_TAIL: длина буфера — round(секунд × частоту),
           а Web Audio, дочитав до номера сэмпла ≥ длины, считает буфер
           кончившимся. При округлении вниз последний сэмпл выхода в окне
           выходил тишиной — дырка в один сэмпл на каждом стыке окон и на
           каждом разрезе. Где кончить, решает duration отрезка, не буфер. */
        var a = clipTime(j, sp.p0), b = clipTime(j, sp.p1) + WIN_TAIL;
        var e = winCache[j];
        if (e && e.a <= a && e.b >= b) return e.buf;
        var buf = null;
        try { buf = await fetchWindow(j, a, b); }
        catch (err) {
            winStats.fails++;
            try { window.__ae && window.__ae('feature_use', { slug: SLUG, f: 'wc_audio_window_failed', err: String(err && err.message || err).slice(0, 100) }); } catch (_) {}
            buf = null;
        }
        if (!buf) { winFailed[j] = true; return decodeClipWhole(j); }   // старый путь: целиком, как было
        if (!winCache[j]) winStats.clips++;
        winCache[j] = { a: a, b: b, buf: buf };
        return buf;
    }
    async function decodeBgm() {
        if (bgmTried) return bgmBuf;
        bgmTried = true;
        var firstErr = null;
        try {
            if (!decCtx) decCtx = new OAC(2, sr, sr);
            bgmBuf = await decCtx.decodeAudioData((await opts.bgMusic.file.arrayBuffer()).slice(0));
        } catch (e) { bgmBuf = null; firstErr = e; }
        /* Браузер не читает формат песни (WMA, AIFF, AMR…) — её переводит FFmpeg, как
           окна звука клипов (fetchWindow). До 01.10.2026 такая музыка молча выпадала
           из файла, а с «Заменить звук клипов музыкой» там оставалась тишина. */
        var ffUsed = 0;
        if (!bgmBuf && typeof FFmpegProcessor !== 'undefined' && FFmpegProcessor && typeof FFmpegProcessor.processStreaming === 'function') {
            var res = null;
            ffUsed = 1;
            try {
                res = await FFmpegProcessor.processStreaming(opts.bgMusic.file,
                    ['-vn', '-map', '0:a:0', '-ac', '2', '-ar', String(sr), '-c:a', 'pcm_s16le'], 'vmwc_bgm.wav');
                bgmBuf = await decCtx.decodeAudioData(await res.blob.arrayBuffer());
            } catch (e2) { bgmBuf = null; firstErr = firstErr || e2; }
            finally { if (res && res.url) { try { URL.revokeObjectURL(res.url); } catch (_) {} } }
        }
        try {
            window.__ae && window.__ae('feature_use', { slug: SLUG, f: bgmBuf ? (ffUsed ? 'wc_bgm_audio_ffmpeg' : 'wc_bgm_audio_ok') : 'wc_bgm_audio_failed',
                ext: String(((opts.bgMusic.file && opts.bgMusic.file.name) || '').split('.').pop() || '').toLowerCase().slice(0, 5),
                err: firstErr ? String((firstErr && firstErr.message) || firstErr || '').slice(0, 120) : '' });
        } catch (_) {}
        return bgmBuf;
    }

    /* Per-clip gain envelope as absolute-time breakpoints (piecewise linear) —
       identical semantics to the old scheduling: transition crossfades take
       precedence over the per-clip audio fades on the same edge. */
    function envelopeOf(j) {
        var c = clips[j], s = starts[j], d = effDur[j];
        /* Огибающую прислал вызывающий — громкость, затухания и стыки ленты он
           считает сам, и по ней же звучит его превью. */
        if (c.env && c.env.length >= 2) return { bps: c.env, s: s, d: d, vol: 1 };
        var vol = c.mute ? 0 : (c.volume != null ? c.volume / 100 : 1);
        var inOv = (j > 0) ? overlaps[j - 1] : 0;
        var outOv = (j < n - 1) ? overlaps[j] : 0;
        var inEnd = s, outStart = s + d;
        if (inOv > 0) inEnd = s + inOv;
        else if (c.audioFadeIn > 0) inEnd = s + Math.min(c.audioFadeIn, d);
        if (outOv > 0) outStart = s + d - outOv;
        else if (c.audioFadeOut > 0) outStart = s + Math.max(0, d - c.audioFadeOut);
        if (inEnd > outStart) inEnd = outStart;   // degenerate short clip: fades meet
        var v0 = inEnd > s ? 0 : vol;
        var vEnd = outStart < s + d ? 0 : vol;
        return { bps: [[s, v0], [inEnd, vol], [outStart, vol], [s + d, vEnd]], s: s, d: d, vol: vol };
    }
    function envAt(env, t) {
        var b = env.bps;
        if (t <= b[0][0]) return b[0][1];
        for (var i = 1; i < b.length; i++) {
            if (t <= b[i][0]) {
                var t0 = b[i - 1][0], t1 = b[i][0], v0 = b[i - 1][1], v1 = b[i][1];
                return t1 <= t0 ? v1 : v0 + (v1 - v0) * ((t - t0) / (t1 - t0));
            }
        }
        return b[b.length - 1][1];
    }

    /* Отрезок буфера в контекст окна. Web Audio не принимает when/offset/
       duration меньше нуля — даже −1e-14 — и бросает RangeError, а он рушит
       всю сборку. Смещения выше считаются так, что меньше нуля не бывают
       (тест — tests/js/video-merge-audio-slices.test.mjs); если всё же
       случится, часть отрезка до начала буфера — тишина, и он честно
       сдвигается, а не роняет сборку: событие wc_audio_slice_fixed. */
    var sliceFix = { n: 0, worst: 0 };
    function startSlice(src, when, off, dur) {
        if (off < 0) { sliceFix.n++; sliceFix.worst = Math.max(sliceFix.worst, -off); when -= off; dur += off; off = 0; }
        if (when < 0) { sliceFix.n++; sliceFix.worst = Math.max(sliceFix.worst, -when); off -= when; dur += when; when = 0; }
        if (!(dur > 0)) return;
        src.start(when, off, dur);
    }

    /* Клип j на его звучании sp внутри окна, начатого в секунде выхода c0.
       Смещение в буфере — от одной монотонной функции: у окна (скорость 1)
       это clipTime(p0) минус начало окна, тоже clipTime — для свежего окна
       ровно 0, для более раннего — больше нуля. До 02.10.2026 здесь стояло
       (trimStart − начало окна) + (p0 − s): алгебраически 0, а в плавающей
       точке ±1 ULP, и при отрицательном знаке Web Audio ронял всю сборку
       (/video-editor: −1.42e-14 на переходе после разреза, −9.09e-13 на
       втором окне длинного фильма — 13 сборок подряд у одного человека,
       час кодирования у другого). Цельный буфер растянут в выходную шкалу,
       поэтому секунда клипа делится на скорость. */
    function scheduleClip(ctx, buf, j, sp, c0) {
        var env = envelopeOf(j), s = env.s;
        var playStart = sp.p0, playEnd = sp.p1;
        var src = ctx.createBufferSource(); src.buffer = buf;
        var g = ctx.createGain();
        var when = playStart - c0;
        g.gain.setValueAtTime(envAt(env, playStart), when);
        for (var i = 0; i < env.bps.length; i++) {
            var bt = env.bps[i][0];
            if (bt > playStart && bt < playEnd) g.gain.linearRampToValueAtTime(env.bps[i][1], bt - c0);
        }
        g.gain.linearRampToValueAtTime(envAt(env, playEnd), playEnd - c0); // exact value at the chunk edge (no-op when flat)
        src.connect(g); g.connect(ctx.destination);
        var off = (buf._vmBase != null)
            ? clipTime(j, playStart) - buf._vmBase
            : (clips[j].trimStart || 0) / (clips[j].speed || 1) + (playStart - s);
        startSlice(src, when, off, playEnd - playStart);
    }

    /* Звуковая дорожка ленты целиком одним декодом (музыка/голос короче
       фильма; тот же декодер-лестница, что у клипов). Не прочиталась —
       тишина на её месте и событие, а не молча. */
    var itemBufs = {};
    async function itemBuf(i) {
        if (i in itemBufs) return itemBufs[i];
        try {
            if (!decCtx) decCtx = new OAC(2, sr, sr);
            itemBufs[i] = await decodeWholeOnce(items[i].file);
        } catch (e) {
            itemBufs[i] = null;
            try {
                window.__ae && window.__ae('feature_use', { slug: SLUG, f: 'wc_item_audio_failed',
                    ext: String(((items[i].file && items[i].file.name) || '').split('.').pop() || '').toLowerCase().slice(0, 5),
                    err: String((e && e.message) || e || '').slice(0, 120) });
            } catch (_) {}
        }
        return itemBufs[i];
    }
    /* Дорожка i в окне [c0, c1): место на ленте [start, start+len), из
       источника — отрезок [trimStart, trimEnd), при loop — по кругу. */
    function scheduleItem(ctx, buf, it, c0, c1) {
        var s = +it.start || 0, e = s + (+it.len || 0);
        var p0 = Math.max(s, c0), p1 = Math.min(e, c1);
        if (p1 - p0 <= 1e-6) return;
        var segA = Math.max(0, +it.trimStart || 0);
        var segB = (it.trimEnd > segA) ? Math.min(+it.trimEnd, buf.duration) : buf.duration;
        var seg = segB - segA;
        if (!(seg > 0.01)) return;
        var env = { bps: (it.env && it.env.length >= 2) ? it.env : [[s, it.volume != null ? +it.volume : 1], [e, it.volume != null ? +it.volume : 1]] };
        var g = ctx.createGain(); g.connect(ctx.destination);
        g.gain.setValueAtTime(envAt(env, p0), p0 - c0);
        for (var b = 0; b < env.bps.length; b++) {
            var bt = env.bps[b][0];
            if (bt > p0 && bt < p1) g.gain.linearRampToValueAtTime(env.bps[b][1], bt - c0);
        }
        g.gain.linearRampToValueAtTime(envAt(env, p1), p1 - c0);
        var p = p0;
        while (p < p1 - 1e-6) {
            var off = p - s;
            if (it.loop) { off = off % seg; if (seg - off < 1e-6) off = 0; }   // граница круга: не «пустой кусок» и break, а новый круг
            else if (off >= seg - 1e-6) break;
            var dur = Math.min(seg - off, p1 - p);
            if (!(dur > 1e-6)) break;
            var src = ctx.createBufferSource(); src.buffer = buf; src.connect(g);
            startSlice(src, p - c0, segA + off, dur);
            p += dur;
            if (!it.loop) break;
        }
    }

    /* Музыка в окне [c0, c1). Без start/env — как всегда: песня с нуля выхода,
       ровная громкость. start — секунда выхода, где звучит начало песни;
       env — огибающая [[t, g], …] в секундах выхода, g — доля громкости
       музыки (план VideoMergeMusicPlan: на клипах «без музыки» она затихает). */
    function scheduleBgm(ctx, bb, c0, c1) {
        var bvol = (opts.bgMusic.volume != null ? opts.bgMusic.volume : 30) / 100;
        var bbDur = bb.duration;
        if (!(bbDur > 0)) return;
        var m0 = Math.max(0, +opts.bgMusic.start || 0);
        var p0 = Math.max(c0, m0);
        if (c1 - p0 <= 1e-6) return;
        var g = ctx.createGain(); g.connect(ctx.destination);
        var benv = opts.bgMusic.env;
        if (benv && benv.length >= 2) {
            var E = { bps: benv };
            g.gain.setValueAtTime(bvol * envAt(E, p0), p0 - c0);
            for (var b = 0; b < benv.length; b++) {
                var bt = benv[b][0];
                if (bt > p0 && bt < c1) g.gain.linearRampToValueAtTime(bvol * benv[b][1], bt - c0);
            }
            g.gain.linearRampToValueAtTime(bvol * envAt(E, c1), c1 - c0);
        } else g.gain.value = bvol;
        if (opts.bgMusic.loop) {
            var p = p0;
            while (p < c1 - 1e-6) {
                var off = (p - m0) % bbDur;
                /* На границе круга округление оставляет off чуть меньше bbDur: dur ≈ 4e-16,
                   p не двигается, и цикл крутится вечно, замораживая вкладку посреди склейки
                   (ревью 01.10: 17–27 % случайных лент с повтором). Это начало нового круга. */
                if (bbDur - off < 1e-6) off = 0;
                var dur = Math.min(bbDur - off, c1 - p);
                var src = ctx.createBufferSource(); src.buffer = bb; src.connect(g);
                startSlice(src, p - c0, off, dur);
                p += dur;
            }
        } else if (p0 - m0 < bbDur) {
            var s2 = ctx.createBufferSource(); s2.buffer = bb; s2.connect(g);
            startSlice(s2, p0 - c0, p0 - m0, Math.min(bbDur - (p0 - m0), c1 - p0));
        }
    }

    var seg = 0, wpos = null;
    return {
        sampleRate: sr,
        channels: 2,
        next: async function () {
            while (seg < n) {
                var k = seg;
                var c0 = starts[k];
                var c1 = (k < n - 1) ? starts[k + 1] : total;
                if (c1 - c0 <= 1e-6) { seg++; wpos = null; continue; }   // fully-overlapped degenerate segment
                // Окно ≤ WIN секунд внутри сегмента; хвостик короче секунды — в текущее окно.
                var w0 = (wpos == null) ? c0 : wpos;
                var w1 = Math.min(c1, w0 + WIN);
                if (c1 - w1 < 1) w1 = c1;
                /* Длина окна — разность номеров сэмплов его краёв, а не
                   ceil(длины): файл кладёт окна встык, и ceil сдвигал весь
                   дальнейший звук на долю сэмпла позже на каждом стыке, а сдвиг
                   копился (до 02.10.2026). Так ошибка места — меньше половины
                   сэмпла и не растёт. Окно короче сэмпла ничего не добавляет. */
                var len = Math.round(w1 * sr) - Math.round(w0 * sr);
                if (len < 1) {
                    if (w1 >= c1 - 1e-6) { seg++; wpos = null; } else { wpos = w1; }
                    continue;
                }
                var ctx = new OAC(2, len, sr);
                var ov = (k > 0) ? overlaps[k - 1] : 0;
                var sp;
                if (ov > 0 && w0 < c0 + ov && (sp = spanIn(k - 1, w0, w1))) {
                    var prevBuf = await getClipBuf(k - 1, sp);
                    if (prevBuf) scheduleClip(ctx, prevBuf, k - 1, sp, w0);
                }
                if ((sp = spanIn(k, w0, w1))) {
                    var curBuf = await getClipBuf(k, sp);
                    if (curBuf) scheduleClip(ctx, curBuf, k, sp, w0);
                }
                if (opts.bgMusic && opts.bgMusic.file) {
                    var bb = await decodeBgm();
                    if (bb) scheduleBgm(ctx, bb, w0, w1);
                }
                for (var ii = 0; ii < items.length; ii++) {
                    var its = +items[ii].start || 0, ite = its + (+items[ii].len || 0);
                    if (ite <= w0 + 1e-6 || its >= w1 - 1e-6) continue;
                    var ib = await itemBuf(ii);
                    if (ib) scheduleItem(ctx, ib, items[ii], w0, w1);
                }
                var rendered = await ctx.startRendering();
                if (w1 >= c0 + ov - 1e-6) { delete decCache[k - 1]; delete winCache[k - 1]; }   // хвост k-1 больше никому не нужен
                if (w1 >= c1 - 1e-6) { seg++; wpos = null; } else { wpos = w1; }
                return rendered;
            }
            decCache = {}; winCache = {}; bgmBuf = null; itemBufs = {};
            if (sliceFix.n) {
                try { window.__ae && window.__ae('feature_use', { slug: SLUG, f: 'wc_audio_slice_fixed', n: sliceFix.n, worst_us: Math.round(sliceFix.worst * 1e6) }); } catch (_) {}
            }
            if (winStats.windows || winStats.fails) {
                try { window.__ae && window.__ae('feature_use', { slug: SLUG, f: 'wc_audio_window', clips: winStats.clips, n: winStats.windows, ms: winStats.ms, fails: winStats.fails, win: WIN, wc: winStats.wc || 0, ff: winStats.ff || 0, whole: winStats.whole || 0 }); } catch (_) {}
            }
            return null;
        }
    };
}

/* Incremental WAV assembly for the SPLIT path: one preallocated 16-bit PCM byte
   array of the exact final size, filled chunk by chunk from the same provider —
   the whole-timeline float PCM never exists in memory. */
async function wavFromChunks(provider, totalSec, throwIfAborted) {
    var sr = provider.sampleRate, nch = 2;
    var totalFrames = Math.max(1, Math.ceil(totalSec * sr));
    var bytes = 44 + totalFrames * nch * 2;
    var out = new Uint8Array(bytes);
    var dv = new DataView(out.buffer);
    var ws = function (o, s) { for (var i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); };
    ws(0, 'RIFF'); dv.setUint32(4, bytes - 8, true); ws(8, 'WAVE'); ws(12, 'fmt ');
    dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, nch, true);
    dv.setUint32(24, sr, true); dv.setUint32(28, sr * nch * 2, true); dv.setUint16(32, nch * 2, true);
    dv.setUint16(34, 16, true); ws(36, 'data'); dv.setUint32(40, totalFrames * nch * 2, true);
    var frame = 0;
    for (;;) {
        throwIfAborted();
        var chunk = await provider.next();
        if (!chunk) break;
        var len = Math.min(chunk.length, totalFrames - frame);
        if (len <= 0) break;
        var ch0 = chunk.getChannelData(0);
        var ch1 = chunk.numberOfChannels > 1 ? chunk.getChannelData(1) : ch0;
        var o = 44 + frame * nch * 2;
        for (var i = 0; i < len; i++) {
            var a = Math.max(-1, Math.min(1, ch0[i])); dv.setInt16(o, a < 0 ? a * 0x8000 : a * 0x7FFF, true); o += 2;
            var b = Math.max(-1, Math.min(1, ch1[i])); dv.setInt16(o, b < 0 ? b * 0x8000 : b * 0x7FFF, true); o += 2;
        }
        frame += len;
    }
    return out;
}

/* ───────── main merge ───────── */
async function merge(clips, transitions, target, opts, hooks) {
    opts = opts || {}; hooks = hooks || {};
    /* Лента фиксируется в момент вызова. Вызывающий передаёт СВОИ массивы, а
       работа идёт минутами: /video-merge отдавал живой список карточек, и
       клип, убранный посреди склейки (обращение #1794, 29.09.2026: третий
       ролик снят, пока полоса стояла на 18 %), исчезал из-под индексов,
       посчитанных на старте, — `Cannot read properties of undefined (reading
       'file')` на стыке, где движок брал следующий клип. Копии массивов
       держат индексы верными при любом вызывающем; не менять сами клипы во
       время работы — забота страницы (она запирает редактор). */
    clips = Array.prototype.slice.call(clips || []);
    transitions = Array.prototype.slice.call(transitions || []);
    var signal = hooks.signal, onProgress = hooks.onProgress || function () {};
    function abort() { if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError'); }
    abort();

    var W = target.tw, H = target.th, fps = target.fps, n = clips.length;
    var fit = (opts.fit === 'cover') ? 'cover' : 'contain';
    // timeline
    var effDur, overlaps, starts;
    var LO = opts.layout;
    if (LO && LO.starts && LO.effDur && LO.overlaps && LO.starts.length === n && LO.effDur.length === n &&
        LO.overlaps.length === Math.max(0, n - 1)) {
        /* Раскладку прислал вызывающий (монтажная лента): её же числами живёт
           его превью, а звук и кадры здесь обязаны совпасть с тем, что человек
           видел на экране. Второй формулы стыков тут быть не должно. */
        effDur = LO.effDur.map(Number); overlaps = LO.overlaps.map(Number); starts = LO.starts.map(Number);
    } else {
        effDur = clips.map(function (c) { var s = c.trimStart || 0, e = c.trimEnd || c.duration; return Math.max(0, e - s) / (c.speed || 1); });
        overlaps = []; for (var i = 0; i < n - 1; i++) {
            var t = transitions[i];
            if (!t || t.type === 'none') { overlaps.push(0); continue; }
            var room = Math.min(effDur[i], effDur[i + 1]) - 0.15;
            var td = Math.min(t.duration || 1, room); overlaps.push(td >= 0.2 ? td : 0);
        }
        starts = [0]; for (var k = 1; k < n; k++) starts.push(starts[k - 1] + effDur[k - 1] - overlaps[k - 1]);
    }
    var total = starts[n - 1] + effDur[n - 1];

    // LAZY DECODER POOL — open a clip's decoder just before its segment, close it the
    // moment its segment (incl. trailing transition) has fully played. A transition only
    // ever blends clip i and i+1, so at most 2 decoders + 2 demux buffers are live at
    // once, regardless of clip count. This avoids both the per-browser concurrent-decoder
    // cap and holding N sample buffers (the prior version opened all N up front). demux is
    // on-demand (full); the encoder awaits drawFrame, so the ~one-time demux pause at a
    // clip boundary never drops a frame.
    var pool = {};            // k -> ClipCursor
    var poolPending = {};     // k -> Promise<ClipCursor>  (dedupe concurrent ensures)
    var peakDecoders = 0;     // high-water mark of simultaneously-open decoders (proves the bound)
    /* Demux LOOKAHEAD — start reading/parsing clip k+1 while clip k plays. Without
       it the encoder sits idle for the seconds a big file takes to demux at each
       clip boundary; an idle codec in a backgrounded tab is what the browser
       reclaims ("Codec reclaimed due to inactivity", 3 sessions/wk). The cache
       holds ≤2 entries of COMPRESSED samples; decoders still open lazily (≤2). */
    var demuxCache = {};      // k -> Promise<demux result>
    function demuxAhead(k) {
        if (k >= n || demuxCache[k]) return;
        if (clipIsImage(clips[k])) return;                // фото не демуксится
        var p = demuxClip(clips[k].file, false, opts);
        p.catch(function () { delete demuxCache[k]; });   // failed prefetch → retry live at need
        demuxCache[k] = p;
    }
    /* Фото на ленте: «курсор», который на любой момент отдаёт одну и ту же
       картинку. Декодирует её вызывающий (clip.image()) — он знает размер
       кадра вывода и не держит 12-Мп снимок целиком ради кадра 1080p. */
    function imageCursor(k) {
        var c = clips[k], img = null;
        return (async function () {
            img = await (typeof c.image === 'function' ? c.image() : c.image);
            return {
                frameAt: async function () { return img; },
                close: function () {
                    if (img && typeof c.releaseImage === 'function') { try { c.releaseImage(img); } catch (e) {} }
                    img = null;
                }
            };
        })();
    }
    function ensureCursor(k) {
        if (pool[k]) return Promise.resolve(pool[k]);
        if (poolPending[k]) return poolPending[k];
        var c = clips[k];
        if (clipIsImage(c)) {
            poolPending[k] = imageCursor(k).then(function (cur) {
                pool[k] = cur; delete poolPending[k];
                demuxAhead(k + 1);
                return cur;
            });
            return poolPending[k];
        }
        poolPending[k] = (async function () {
            var d = await (demuxCache[k] || demuxClip(c.file, false, opts));
            delete demuxCache[k];
            var startUs = Math.round((c.trimStart || 0) * 1e6);
            var endUs = c.trimEnd != null ? Math.round(c.trimEnd * 1e6) : null;
            /* Кадры пула рисует drawClipFrame или compose /video-editor — оба через
               VideoOrientation, копия им не нужна. */
            var cur = new ClipCursor(new DecodeStream(d, startUs, endUs, { deferRotation: true }), fps);
            pool[k] = cur; delete poolPending[k];
            peakDecoders = Math.max(peakDecoders, Object.keys(pool).length);
            demuxAhead(k + 1);
            return cur;
        })();
        return poolPending[k];
    }
    function releaseDone(tSec) {
        var keys = Object.keys(pool);
        for (var i = 0; i < keys.length; i++) {
            var k = +keys[i];
            if (tSec > starts[k] + effDur[k] + 0.05) { try { pool[k].close(); } catch (e) {} delete pool[k]; }
        }
    }

    // active-clip resolver for an output time
    function activeAt(tSec) {
        var a = [];
        for (var q = 0; q < n; q++) if (tSec >= starts[q] - 1e-6 && tSec < starts[q] + effDur[q] + 1e-6) a.push(q);
        return a;
    }
    var mappedTypes = {};

    /* Наложение поверх готового кадра (надписи /video-editor). Крючок, а не
       ветка в движке: движок склейки не должен знать, что бывают надписи, а
       вызывающий не должен ради них заводить второй проход кодирования.
       Зовётся ПОСЛЕ композиции и ДО releaseDone во ВСЕХ трёх ветках — включая
       «клипа нет» (чёрный кадр): титр на чёрном экране это законный кадр, и
       забыть его в одной ветке значит потерять надпись в конкретном месте
       ленты, а не везде — то есть незаметно. */
    var onOverlay = typeof hooks.onOverlay === 'function' ? hooks.onOverlay : null;
    function overlay(ctx, tSec) { if (onOverlay) { try { onOverlay(ctx, tSec, W, H); } catch (e) {} } }

    /* Кадр целиком рисует вызывающий (монтажная лента): раскладку, переходы,
       фон, скрытие и надписи он делает ОДНИМ кодом для превью и для файла.
       Отсюда ему нужны только кадры клипов — frameOf(k, srcSec) отдаёт кадр
       клипа k на секунде ИСТОЧНИКА (кадр принадлежит курсору: нарисовать и не
       хранить). Ошибка рисования здесь не глушится: молча чёрный кадр в файле
       хуже честного отказа. */
    var compose = typeof hooks.compose === 'function' ? hooks.compose : null;
    function frameOf(k, srcSec) {
        return ensureCursor(k).then(function (cur) { return cur.frameAt(Math.round((srcSec || 0) * 1e6)); });
    }

    var drawFrame = async function (ctx, idx, tSec) {
        abort();
        if (compose) {
            await compose(ctx, tSec, W, H, frameOf);
            releaseDone(tSec);
            return;
        }
        var act = activeAt(tSec);
        /* A hard join (no transition, zero overlap): both clips are «active» at the
           join only through the ±1e-6 tolerance, and the overlap branch below drew the
           OUTGOING one — the picture just past its end (a held last frame, or in
           /video-trim's cut the first frame of the deleted span), while the incoming
           clip's first frame was never shown. Measured 21.10.2026, a cut [40.5, 55.3]
           of a 30 fps file: frames 0…1215 | 1661… against FFmpeg's 0…1214 | 1660….
           The join instant belongs to the clip that starts there. */
        if (act.length === 2 && !(overlaps[act[0]] > 0)) act = [act[1]];
        if (act.length === 0) { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H); overlay(ctx, tSec); releaseDone(tSec); return; }
        if (act.length === 1) {
            var k = act[0];
            var cur = await ensureCursor(k);
            var f = await cur.frameAt(Math.round(((c0(k).trimStart || 0) + (tSec - starts[k]) * (c0(k).speed || 1)) * 1e6));
            if (f) drawClipFrame(ctx, f, c0(k).rotation || 0, W, H, fit); else { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H); }
            overlay(ctx, tSec);
            releaseDone(tSec);
            return;
        }
        // overlap — composite the latest active pair (act[0]=outgoing, act[1]=incoming)
        var ka = act[0], kb = act[1], t = transitions[ka] || { type: 'fade' };
        var ov = overlaps[ka] || (transitions[ka] && transitions[ka].duration) || 1;
        var prog = Math.min(1, Math.max(0, (tSec - starts[kb]) / ov));
        var curA = await ensureCursor(ka), curB = await ensureCursor(kb);
        var fa = await curA.frameAt(Math.round(((c0(ka).trimStart || 0) + (tSec - starts[ka]) * (c0(ka).speed || 1)) * 1e6));
        var fb = await curB.frameAt(Math.round(((c0(kb).trimStart || 0) + (tSec - starts[kb]) * (c0(kb).speed || 1)) * 1e6));
        /* колбэки рисуют на ПЕРЕДАННЫЙ контекст: пиксельные эффекты читают кадры
           со своих холстов (см. compositeTransition) */
        var used = compositeTransition(ctx,
            function (g) { if (fa) drawClipFrame(g, fa, c0(ka).rotation || 0, W, H, fit); else { g.fillStyle = '#000'; g.fillRect(0, 0, W, H); } },
            function (g) { if (fb) drawClipFrame(g, fb, c0(kb).rotation || 0, W, H, fit); else { g.fillStyle = '#000'; g.fillRect(0, 0, W, H); } },
            prog, t.type, W, H);
        if (used && used.indexOf('fade*') === 0) mappedTypes[t.type] = used;
        overlay(ctx, tSec);
        releaseDone(tSec);
    };
    function c0(i) { return clips[i]; }

    // Output format: the probe may have overridden the user's container choice
    // (no-H.264 browsers → 'webm'); the caller renames the download accordingly.
    var fmt = opts.outFormat || opts.format || 'mp4';
    if (fmt !== 'webm' && fmt !== 'mp4') fmt = 'mp4';   // mov/mkv output goes via the FFmpeg engine, not here
    var crf = opts.crf != null ? opts.crf : 18;

    // probe audio capability to decide FAST vs SPLIT
    var vidProbe = await window.CanvasVideoExport.isSupported({ format: fmt, width: W, height: H, fps: fps, audioSampleRate: 48000, audioChannels: 2 });
    var canFullAV = vidProbe.ok;    // video+audio encoders present → FAST (single pass)

    /* Pre-flight size honesty — fail in SECONDS with a typed error the UI can
       explain, not after a 20-minute encode (the fokus33 experience):
       • SPLIT (no AAC encoder → FFmpeg muxes the audio): the muxed output must fit
         WASM MEMFS (~2 GB heap, shared with FFmpeg itself) — cap ≈1.1 GB.
       • FAST: the streaming path needs OPFS headroom for the output file. */
    var estBytes = window.CanvasVideoExport.estimateBytes
        ? window.CanvasVideoExport.estimateBytes(W, H, fps, crf, total, true)
        : ((W * H * fps * 0.16 + 192000) / 8) * total;
    /* SPLIT glues the sound on through CanvasVideoExport.muxAudioInto, whose
       FFmpeg holds the video in memory up to remuxMaxBytes: past it, fail now
       with a typed error, not after the whole encode. */
    var splitCap = window.CanvasVideoExport.remuxMaxBytes || (400 * 1048576);
    if (!canFullAV && estBytes > splitCap) {
        var tooLarge = new Error('split-output-too-large:' + Math.round(estBytes / 1048576) + 'MB');
        tooLarge.code = 'SPLIT_TOO_LARGE'; tooLarge.capMB = Math.round(splitCap / 1048576);
        throw tooLarge;
    }
    /* No disk pre-flight here any more (2026-09-25). It read
       navigator.storage.estimate(), which on most Chromium builds answers
       usage + 10 GiB whatever is really left — blind exactly where the disk was
       full — and where it did see (Safari, Firefox) it refused merges that fit
       in memory. CanvasVideoExport now RESERVES the room before frame 0
       (__opfsMuxLane): a refused reservation encodes into memory, or, past the
       memory ceiling, throws `output-too-large-no-opfs` with `diskFull` within
       seconds — the same early, typed answer this block existed to give. */

    /* Ожидание звука должно быть видно. Окно звука клипа готовится, пока кадры
       стоят (кодировщик просит звук уже на первой секунде ленты), а полоса
       прогресса движется только кадрами: у #1794 (29.09.2026) она простояла на
       18 % 135 с, пока FFmpeg читал звук 201-МБ клипа. hooks.onAudioWait(true) —
       ждём дольше 1,5 с, (false, ms) — дождались. */
    function watchAudio(p) {
        if (!p || typeof hooks.onAudioWait !== 'function') return p;
        var inner = p.next;
        p.next = async function () {
            var t0 = Date.now(), shown = false;
            var t = setTimeout(function () { shown = true; try { hooks.onAudioWait(true, 0); } catch (_) {} }, 1500);
            try { return await inner.apply(p, arguments); }
            finally { clearTimeout(t); if (shown) { try { hooks.onAudioWait(false, Date.now() - t0); } catch (_) {} } }
        };
        return p;
    }

    var blob;
    /* opts.bitrate (bit/s) — explicit video bitrate for the encoder; the caller
       that normalizes ONE clip next to byte-copied ones derives it from the
       source's own bit density so the part does not drop below its source.
       Absent → the crf→bpp mapping, as before. */
    var vBitrate = (opts.bitrate > 0) ? Math.round(opts.bitrate) : undefined;
    /* Where the finished file goes, when the caller has a say (the editor's way
       out of a full system disk, report 12.10.2026): opts.outputHandle — a file
       the person picked, the encoder writes straight into it (the result then
       carries __savedToPicked); opts.noDiskTarget — into memory, no temp file.
       The SPLIT lane hands its video to FFmpeg, so only «memory» applies there:
       the caller writes the returned blob into the picked file itself. */
    var noDisk = opts.noDiskTarget === true;
    try {
        if (canFullAV) {
            var chunks = watchAudio(makeAudioChunks(clips, transitions, starts, effDur, overlaps, opts, 48000));
            blob = await window.CanvasVideoExport.encode({ width: W, height: H, fps: fps, durationSec: total,
                format: fmt, qualityCrf: crf, bitrate: vBitrate, audioBuffer: null, audioChunks: chunks, drawFrame: drawFrame, signal: signal, h264Only: !!opts.h264Only,
                outputHandle: opts.outputHandle || null, noDiskTarget: noDisk,
                onProgress: function (p) { onProgress(p * 0.98); } });
        } else {
            // SPLIT: WebCodecs video-only + FFmpeg AAC mux of the premixed PCM
            var videoBlob = await window.CanvasVideoExport.encode({ width: W, height: H, fps: fps, durationSec: total,
                format: fmt, qualityCrf: crf, bitrate: vBitrate, audioBuffer: null, drawFrame: drawFrame, signal: signal, h264Only: !!opts.h264Only,
                noDiskTarget: noDisk,
                onProgress: function (p) { onProgress(p * 0.85); } });
            var provider = watchAudio(makeAudioChunks(clips, transitions, starts, effDur, overlaps, opts, 48000));
            if (!provider) { blob = videoBlob; }
            else {
                abort();
                onProgress(0.88);
                var wav = await wavFromChunks(provider, total, abort);
                /* The sound goes on through the exporter's own door (2026-10-30).
                   This lane carried a second copy of it that called
                   FFmpegProcessor.init() on the page's STUB — the real module is
                   loaded by __ensureFFmpeg, which the copy never called — so it
                   waited 15 s and threw «module never loaded» AFTER the whole
                   video had been encoded: RU, October, /video-editor — 18 sessions
                   (Firefox, Chromium on Linux, Yandex on Linux), not one file out
                   of this lane in a month; people pressed again and again. The
                   door loads the engine itself, writes the length of the VIDEO
                   (apad + shortest), moov at the head, and returns null — never a
                   half-made file — when it cannot. */
                var muxed = await window.CanvasVideoExport.muxAudioInto(videoBlob,
                    new File([wav], 'timeline.wav', { type: 'audio/wav' }),
                    { format: fmt === 'webm' ? 'webm' : 'mp4', signal: signal, audioBitrate: 192000, durationSec: total });
                wav = null;
                abort();
                if (!muxed) {
                    var noMux = new Error('split-audio-mux-failed');
                    noMux.code = 'SPLIT_AUDIO_MUX_FAILED';
                    throw noMux;
                }
                blob = muxed;
            }
        }
    } finally {
        var openKeys = Object.keys(pool);
        for (var z = 0; z < openKeys.length; z++) { try { await pool[openKeys[z]].close(); } catch (e) {} }
        pool = {}; poolPending = {}; demuxCache = {};
    }
    onProgress(1);
    if (hooks.onTransitionMapped && Object.keys(mappedTypes).length) hooks.onTransitionMapped(mappedTypes);
    if (hooks.onStats) try { hooks.onStats({ peakDecoders: peakDecoders, clips: n }); } catch (e) {}
    return blob;
}

window.VideoMergeWebCodecs = { probe: probe, merge: merge, _isMp4Family: isMp4Family,
    // Low-level WebCodecs demux/decode primitives, exposed so other tools (split-screen's
    // decoder-pull export) reuse the same proven, lazy, bounded decode path instead of
    // re-implementing it. Additive — does not change merge() behaviour.
    demux: demux, demuxLazy: demuxLazy, DecodeStream: DecodeStream, ClipCursor: ClipCursor,
    // WebM / Matroska in the same lazy shape — a VP8/VP9/AV1/H.264 clip decodes like an mp4 one.
    demuxMatroskaLazy: demuxMatroskaLazy, isMatroska: isMatroska,
    // The one answer to «demux this clip for decoding»: mp4 lazily (eager only
    // under opts.eagerMax), WebM/MKV with opts.matroska. Used by the frame
    // source of the per-frame tools (video-bg-remover/frame-source.js).
    demuxClip: demuxClip,
    // The looping flavour — the piece split-screen's Strategy B documents as
    // missing. Used by /audio-to-video's video background.
    LoopingClipCursor: LoopingClipCursor, clipDurationUs: clipDurationUs, sourceFpsOf: sourceFpsOf,
    presentationTimesUs: presentationTimesUs,
    trackRotationDeg: trackRotationDeg,
    loadMp4Box: loadMp4Box, isMp4Family: isMp4Family,
    // Shared so every probe/configure shapes its VideoDecoderConfig identically —
    // a null `description` throws TypeError instead of reporting "unsupported".
    decoderConfig: decoderConfig, hasUsableDims: hasUsableDims,
    /* Композитор переходов и «вписать кадр в кадр». Вынесены наружу 2026-09-06
       для /video-insert: врезка клипа — тот же стык двух источников с xfade, и
       ВТОРАЯ копия этих 40 строк разъехалась бы с первой молча (ровно так у
       тула уже была своя карта переходов, не совпадавшая с этой). Аддитивно:
       merge() зовёт те же функции. */
    compositeTransition: compositeTransition, drawClipFrame: drawClipFrame,
    transitionIsNative: function (t) { return !!KNOWN[String(t || '').toLowerCase()]; },
    // Все эффекты, которые композитор рисует по-своему (имена xfade), и сам перенос
    // формул — для сверки с FFmpeg (tests/js/xfade-transitions-match-ffmpeg.test.mjs).
    transitionIds: XFADE_IDS.slice(), xfadePixels: xfadePixels,
    // The project's single decoder-description builder. Hand-packing the record
    // was measured wrong on 100% of real files (see writeConfigBox above), so any
    // module still carrying its own copy should adopt one of these two.
    buildDecoderDescription: buildDecoderDescription, descriptionFromTrak: descriptionFromTrak };
})();
